import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/services/api';
import { storeSession } from '@/services/authSession';
import { pushService } from '@/services/pushService';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';
import useImageCropper from '@/hooks/useImageCropper';
import { compressImage, compressAvatar, compressDocument } from '@/utils/imageCompress';
import { formatDateAR, formatDateTimeAR, formatMonthShortAR, getDayAR } from '@/utils/dateFormat';
import ImageLightbox from '@/components/ImageLightbox';
import NotificationsPanel from '@/components/NotificationsPanel';
import { buildGuestNotificationItems, getGuestNotificationUnreadCount } from '@/utils/guestNotifications';
import Avatar from '@/components/Avatar';
import ClaimChatModal from '@/components/ClaimChatModal';
import DirectChatModal from '@/components/DirectChatModal';
import LoadingIndicator from '@/components/LoadingIndicator';
import ContractRenewalModal from '@/components/ContractRenewalModal';
import SurveyFlow from '@/components/SurveyFlow';
import InfoPopupModal from '@/components/InfoPopupModal';
import InstallGuideModal from '@/components/InstallGuideModal';
import { getRenewalEligibility } from '@/utils/renewalEligibility';
import { linkify } from '@/utils/linkify';
import { 
  Home, House, Bell, AlertTriangle, User, UserRound, LogOut, Plus, 
  Clock, Wrench, Sparkles, Volume2, HelpCircle, LifeBuoy, LockKeyhole,
  Calendar, CalendarDays, MapPin, BellRing, Gift, Users, UsersRound, Building2,
  Camera, Instagram, Facebook, Globe, Edit2, X, Check, Smartphone,
  ChevronRight, ChevronDown, UserRoundPlus, Plane, Sun, Moon, LayoutGrid,
  BarChart3, FileText, Phone, MessageSquare, Megaphone,
  Info, CircleAlert, Siren, FileWarning,
  Dumbbell, Laptop, Flame, WashingMachine, Sofa, BookOpen, Tv
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import './GuestDashboard.css';

const claimTypeIcons = {
  repair: <Wrench className="w-4 h-4" />,
  cleaning: <Sparkles className="w-4 h-4" />,
  noise: <Volume2 className="w-4 h-4" />,
  other: <HelpCircle className="w-4 h-4" />
};

const claimTypeLabels = { repair: 'Reparación', cleaning: 'Limpieza', noise: 'Convivencia', other: 'Otro' };
const claimTypeLabelsEn = { repair: 'Repair', cleaning: 'Cleaning', noise: 'Coexistence', other: 'Other' };
const statusLabels = { pending: 'Pendiente', in_progress: 'En progreso', resolved: 'Resuelto', closed: 'Cerrado' };
const statusLabelsEn = { pending: 'Pending', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };

const spaceTypeIcons = {
  gym: Dumbbell,
  coworking: Laptop,
  bbq: Flame,
  laundry: WashingMachine,
  living: Sofa,
  study: BookOpen,
  tv: Tv,
  other: Building2,
};

const getSpaceIcon = (spaceType) => spaceTypeIcons[spaceType] || Building2;

const localDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const visitDurationNights = (raw) => {
  const m = /^(\d+)\s*nights?$/i.exec(raw || '');
  if (m) return parseInt(m[1], 10);
  if (raw === 'weekend') return 2;
  if (raw === '1 week') return 7;
  return 1;
};

const isHistoricalGuestVisit = (visit, todayStr = localDateKey(new Date())) => {
  if (visit.status === 'rejected' || visit.status === 'cancelled') return true;
  if (!visit.visit_date) return false;
  const start = new Date(`${visit.visit_date}T00:00:00`);
  if (Number.isNaN(start.getTime())) return false;
  const end = new Date(start);
  end.setDate(end.getDate() + visitDurationNights(visit.visit_duration));
  return localDateKey(end) < todayStr;
};

const getGuestVisitGroups = (visits = []) => {
  const todayStr = localDateKey(new Date());
  const active = visits
    .filter(visit => !isHistoricalGuestVisit(visit, todayStr))
    .sort((a, b) => new Date(`${a.visit_date || '9999-12-31'}T00:00:00`) - new Date(`${b.visit_date || '9999-12-31'}T00:00:00`));
  const historical = visits
    .filter(visit => isHistoricalGuestVisit(visit, todayStr))
    .sort((a, b) => new Date(`${b.visit_date || '0001-01-01'}T00:00:00`) - new Date(`${a.visit_date || '0001-01-01'}T00:00:00`));
  return { active, historical };
};

const getGuestClaimGroups = (claims = []) => ({
  active: claims
    .filter(claim => claim.status !== 'closed')
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
  closed: claims
    .filter(claim => claim.status === 'closed')
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
});

const getUpcomingGuestReservations = (reservations = [], now = new Date()) => reservations
  .filter(reservation => reservation.status !== 'cancelled')
  .filter(reservation => {
    const end = new Date(`${reservation.date}T${reservation.end_time || '23:59'}`);
    return !Number.isNaN(end.getTime()) && end >= now;
  })
  .sort((a, b) => new Date(`${a.date}T${a.start_time || '00:00'}`) - new Date(`${b.date}T${b.start_time || '00:00'}`));

const announcementPriorityVisuals = {
  info: { icon: Info, label: 'Información' },
  important: { icon: CircleAlert, label: 'Importante' },
  urgent: { icon: Siren, label: 'Urgente' },
  maintenance: { icon: Wrench, label: 'Mantenimiento' },
  event: { icon: CalendarDays, label: 'Evento' },
  multa: { icon: FileWarning, label: 'Multa / Sanción' },
};

const getAnnouncementPriorityVisual = (priority) => (
  announcementPriorityVisuals[priority] || announcementPriorityVisuals.info
);




function useBodyScrollLock(enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [enabled]);
}

function NativeDateField({ value, onChange, language = 'es', className = '', ...inputProps }) {
  const displayValue = value
    ? value.split('-').reverse().join('/')
    : (language === 'es' ? 'Seleccionar fecha' : 'Select date');

  return (
    <div className={`app-date-field ${className}`.trim()}>
      <div className={`app-date-field__visual${value ? '' : ' app-date-field__visual--placeholder'}`} aria-hidden="true">
        <CalendarDays className="app-date-field__icon" />
        <span>{displayValue}</span>
      </div>
      <input
        {...inputProps}
        type="date"
        value={value}
        onChange={onChange}
        className="app-date-field__native-input"
      />
    </div>
  );
}

export function GuestDashboard() {
  const { user, logout, checkAuth } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('home');
  const [claims, setClaims] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [visits, setVisits] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [houses, setHouses] = useState([]);
  const [events, setEvents] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [publicProfiles, setPublicProfiles] = useState([]);
  const [newGuests, setNewGuests] = useState([]);
  const [communitySeenAt, setCommunitySeenAt] = useState(user?.seen_community_at);
  const [welcomeSeen, setWelcomeSeen] = useState(user?.onboarding_seen ?? false);
  const [welcomeBannerDismissed, setWelcomeBannerDismissed] = useState(!!user?.welcome_banner_dismissed_at);
  const [showTutorial, setShowTutorial] = useState(false);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(true);
  const [showSurvey, setShowSurvey] = useState(false);
  const [activePopups, setActivePopups] = useState([]);
  const [appSettings, setAppSettings] = useState({ renewal_threshold_days: 60, renewal_threshold_1m: 10, renewal_threshold_3m: 30, renewal_threshold_6m: 60, visit_cost_per_night: 0, visit_cost_currency: 'ARS' });
  const [directThreads, setDirectThreads] = useState([]);
  const [openMessagesModal, setOpenMessagesModal] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [announcementDetailOpen, setAnnouncementDetailOpen] = useState(false);
  const directThreadsUnread = directThreads.reduce((acc, t) => acc + (t.unread_guest || 0), 0);
  const unseenNewGuests = newGuests.filter(g => !communitySeenAt || (g.created_at && g.created_at > communitySeenAt));
  const hasUnseenNewGuests = unseenNewGuests.length > 0;
  const isNewUser = (() => {
    if (!user?.created_at) return false;
    const created = new Date(user.created_at).getTime();
    return Number.isFinite(created) && created > (Date.now() - 30 * 24 * 60 * 60 * 1000);
  })();
  const showWelcomeOnboarding = !welcomeSeen && isNewUser;
  const showWelcomeBanner = welcomeSeen && isNewUser && !welcomeBannerDismissed;
  const [loading, setLoading] = useState(true);
  const [showNewClaim, setShowNewClaim] = useState(false);
  const [showNewVisit, setShowNewVisit] = useState(false);
  const [showNewTransfer, setShowNewTransfer] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showQuickActionsModal, setShowQuickActionsModal] = useState(false);
  const [showCustomizeQA, setShowCustomizeQA] = useState(false);
  const [showRenewal, setShowRenewal] = useState(false);
  const [claimsInitialSubTab, setClaimsInitialSubTab] = useState('claims');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifTick, setNotifTick] = useState(0); 
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const goToClaimsSubTab = (subTab) => {
    setClaimsInitialSubTab(subTab);
    setActiveTab('claims');
  };

  
  
  
  
  
  
  const popHandlerRef = useRef(null);
  useEffect(() => {
    window.history.pushState({ wannaApp: 'sentinel' }, '');
    const onPopState = () => {
      const handled = popHandlerRef.current && popHandlerRef.current();
      
      
      
      window.history.pushState({ wannaApp: 'sentinel' }, '');
      void handled;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  
  
  
  
  popHandlerRef.current = () => {
    if (showExitConfirm) { setShowExitConfirm(false); return true; }
    if (showNotifications) { setShowNotifications(false); return true; }
    if (showRenewal) { setShowRenewal(false); return true; }
    if (showQuickActionsModal) { setShowQuickActionsModal(false); return true; }
    if (showCustomizeQA) { setShowCustomizeQA(false); return true; }
    if (showEditProfile) { setShowEditProfile(false); return true; }
    if (showNewTransfer) { setShowNewTransfer(false); return true; }
    if (showNewVisit) { setShowNewVisit(false); return true; }
    if (showNewClaim) { setShowNewClaim(false); return true; }
    if (activeTab !== 'home') { setActiveTab('home'); return true; }
    
    
    setShowExitConfirm(true);
    return true;
  };

  
  
  const [deepLinkAnnouncementId, setDeepLinkAnnouncementId] = useState(null);
  const [deepLinkClaimId, setDeepLinkClaimId] = useState(null);
  const [deepLinkBenefitId, setDeepLinkBenefitId] = useState(null);

  useEffect(() => {
    loadData();
    
  }, []);

  
  
  
  
  
  useEffect(() => {
    const applyDeepLink = (search) => {
      try {
        const params = new URLSearchParams(search);
        const tab = params.get('tab');
        const id = params.get('id');
        
        
        
        if (tab === 'messages') {
          setOpenMessagesModal(true);
          return;
        }
        if (tab && ['home', 'services', 'claims', 'announcements', 'benefits', 'profile'].includes(tab)) {
          setActiveTab(tab);
          if (tab === 'announcements' && id) setDeepLinkAnnouncementId(id);
          if (tab === 'claims' && id) setDeepLinkClaimId(id);
          if (tab === 'benefits' && id) setDeepLinkBenefitId(id);
        }
      } catch (e) {  }
    };
    applyDeepLink(window.location.search);
    const onSwMsg = (event) => {
      const data = event.data || {};
      if (data.type !== 'NAVIGATE' || !data.url) return;
      try {
        const u = new URL(data.url, window.location.origin);
        applyDeepLink(u.search);
      } catch (e) {  }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMsg);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMsg);
      }
    };
    
  }, []);

  
  useAutoRefresh(() => loadData({ silent: true }), 120000);

  const loadData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setSurveyLoading(true);
    try {
      const [claimsData, announcementsData, benefitsData, visitsData, transfersData, housesData, eventsData, reservationsData, settingsData, threadsData, newGuestsData, activeSurveyData, activePopupsData] = await Promise.all([
        api.getClaims(),
        api.getAnnouncements(),
        api.getBenefits(),
        api.getVisits(),
        api.getTransfers(),
        api.getHouses(),
        api.getEvents().catch(() => []),
        api.getReservations().catch(() => []),
        api.getSettings().catch(() => ({ renewal_threshold_days: 60, visit_cost_per_night: 0, visit_cost_currency: 'ARS' })),
        api.listDirectThreads().catch(() => []),
        api.getNewGuests().catch(() => []),
        api.getActiveSurvey().catch(() => null),
        api.getActivePopups().catch(() => []),
      ]);
      const toArray = (v) => Array.isArray(v) ? v : (v?.items ?? v?.data ?? v?.results ?? v?.announcements ?? v?.claims ?? v?.benefits ?? v?.visits ?? v?.transfers ?? v?.houses ?? v?.events ?? v?.reservations ?? []);
      setClaims(toArray(claimsData));
      setAnnouncements(toArray(announcementsData));
      setBenefits(toArray(benefitsData));
      setVisits(toArray(visitsData));
      setTransfers(toArray(transfersData));
      setHouses(toArray(housesData));
      setEvents(toArray(eventsData));
      setReservations(toArray(reservationsData));
      setAppSettings(settingsData || { renewal_threshold_days: 60, visit_cost_per_night: 0, visit_cost_currency: 'ARS' });
      setDirectThreads(Array.isArray(threadsData) ? threadsData : []);
      setNewGuests(Array.isArray(newGuestsData) ? newGuestsData : []);
      setActiveSurvey(activeSurveyData || null);
      setActivePopups(Array.isArray(activePopupsData) ? activePopupsData : []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setSurveyLoading(false);
    }
    if (!silent) setLoading(false);
  };

  const goToCommunity = () => {
    setActiveTab('community');
    const now = new Date().toISOString();
    setCommunitySeenAt(now);
    api.markSeen({ community: now }).catch(() => {});
  };

  const dismissWelcome = () => {
    setWelcomeSeen(true);
    api.markOnboardingSeen().catch(() => {});
  };

  const dismissWelcomeBanner = () => {
    setWelcomeBannerDismissed(true);
    api.dismissWelcomeBanner().catch(() => {});
  };

  const visiblePopups = activePopups.filter((popup) => (
    popup.cta_type !== 'survey' || (!surveyLoading && !!activeSurvey)
  ));
  const currentPopup = visiblePopups[0] || null;

  const advancePopup = () => {
    if (!currentPopup) return;
    setActivePopups((current) => current.filter((popup) => popup.id !== currentPopup.id));
  };

  const handlePopupClose = () => {
    const canDismiss = currentPopup && (
      currentPopup.cta_type !== 'survey' || (!surveyLoading && !!activeSurvey)
    );
    if (canDismiss) api.dismissPopup(currentPopup.id).catch(() => {});
    advancePopup();
  };

  const handlePopupAction = (ctaType) => {
    if (ctaType === 'survey' && (surveyLoading || !activeSurvey)) {
      advancePopup();
      return;
    }
    if (currentPopup) api.dismissPopup(currentPopup.id).catch(() => {});
    advancePopup();
    if (ctaType === 'benefit') {
      setActiveTab('benefits');
    } else if (ctaType === 'survey') {
      setShowSurvey(true);
    }
  };

  const completeProfile = () => {
    dismissWelcome();
    setActiveTab('profile');
    setShowEditProfile(true);
  };

  const showWelcomeTutorial = () => {
    dismissWelcome();
    setShowTutorial(true);
  };

  const goToWelcomeTab = (tab) => {
    dismissWelcome();
    if (tab === 'community') {
      goToCommunity();
    } else {
      setActiveTab(tab);
    }
  };

  const loadPublicProfiles = async () => {
    try {
      const profiles = await api.getPublicProfiles();
      setPublicProfiles(profiles);
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  };

  const handleCreateClaim = async (claimData) => {
    try {
      const newClaim = await api.createClaim(claimData);
      setClaims([newClaim, ...claims]);
      setShowNewClaim(false);
    } catch (error) {
      console.error('Error creating claim:', error);
    }
  };

  const handleCloseClaim = async (claimId) => {
    try {
      const updated = await api.updateClaim(claimId, { status: 'closed' });
      setClaims(claims.map(c => c.id === claimId ? updated : c));
    } catch (error) {
      console.error('Error closing claim:', error);
    }
  };

  const handleCreateVisit = async (visitData) => {
    try {
      const newVisit = await api.createVisit(visitData);
      setVisits([newVisit, ...visits]);
      return newVisit;
    } catch (error) {
      console.error('Error creating visit:', error);
      throw error;
    }
  };

  const handleCreateTransfer = async (transferData) => {
    try {
      const newTransfer = await api.createTransfer(transferData);
      setTransfers([newTransfer, ...transfers]);
      setShowNewTransfer(false);
    } catch (error) {
      console.error('Error creating transfer:', error);
      throw error;
    }
  };

  const handleUpdateProfile = async (profileData) => {
    try {
      await api.updateProfile(profileData);
      await checkAuth();
      setShowEditProfile(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const unreadAnnouncements = announcements.filter(a => !a.read_by?.includes(user?.id));
  const isExclusiveRoom = user?.room_type === 'exclusive';
  
  
  
  
  
  const _renewalEligComputed = getRenewalEligibility(user?.lease_start, user?.lease_end, appSettings);
  const RENEWALS_ENABLED = false;
  const renewalElig = RENEWALS_ENABLED
    ? _renewalEligComputed
    : { eligible: false, daysRemaining: null, durationDays: null, threshold: null };

  
  
  
  
  const visibleNotifications = buildGuestNotificationItems({ announcements, claims, directThreads, userId: user?.id, language });
  const notificationsUnread = getGuestNotificationUnreadCount(visibleNotifications);
  const markClaimReadLocally = (claimId) => {
    if (!claimId) return;
    setClaims((prev) => prev.map((c) => (c.id === claimId ? { ...c, unread_count: 0 } : c)));
    setNotifTick((t) => t + 1);
  };

  const handleMarkAnnouncementRead = async (id) => {
    
    setAnnouncements(prev => prev.map(a =>
      a.id === id
        ? { ...a, read_by: [...(a.read_by || []), user?.id].filter((x, i, arr) => arr.indexOf(x) === i) }
        : a
    ));
    try {
      await api.markAnnouncementRead(id);
    } catch (e) {
      console.error('Error marking as read', e);
    }
  };

  const navItems = [
    { id: 'home', icon: House, label: t('home') },
    { id: 'community', icon: UsersRound, label: t('community') },
    { id: '__center__', icon: null, label: '', isLogo: true },
    { id: 'services', icon: LayoutGrid, label: language === 'es' ? 'Servicios' : 'Services' },
    { id: 'profile', icon: UserRound, label: t('profile') },
  ];

  return (
    <div className="guest-dashboard min-h-screen-safe bg-background">
      {}
      <header className="guest-home__header sticky top-0 z-50">
        <div className="guest-home__header-inner max-w-lg mx-auto">
          <button onClick={() => setActiveTab('home')} className="guest-home__brand" data-testid="header-brand">
            <div className="guest-home__logo-shell">
              <img src="/logo192.png" alt="WannaColive" className="guest-home__logo brand-logo-adaptive" />
            </div>
            <div className="guest-home__brand-copy">
              <p className="guest-home__greeting font-heading text-foreground">
                {language === 'es' ? '¡Hola' : 'Hi'}, {user?.name?.split(' ')[0]}!
              </p>
              <p className="guest-home__residence-meta text-muted-foreground">
                {(() => {
                  const myHouse = houses?.find(h => h.id === user?.house_id);
                  const houseName = myHouse?.name || 'WannaColive';
                  return user?.room_number
                    ? `${houseName} · ${language === 'es' ? 'Hab.' : 'Room'} ${user.room_number}`
                    : houseName;
                })()}
              </p>
            </div>
          </button>
          <div className="guest-home__header-actions">
            <button
              onClick={() => {
                const opening = !showNotifications;
                setShowNotifications(opening);
              }}
              className="guest-home__header-control relative text-foreground"
              aria-label="Notifications"
              data-testid="header-bell"
            >
              <Bell className="w-5 h-5" />
              {notificationsUnread > 0 && (
                <span className="guest-home__notification-badge absolute" data-testid={`notif-badge-${notifTick}`}>
                  {notificationsUnread}
                </span>
              )}
            </button>
            {showNotifications && (
              <NotificationsPanel
                announcements={announcements}
                claims={claims}
                directThreads={directThreads}
                userId={user?.id}
                language={language}
                items={visibleNotifications}
                onClose={() => setShowNotifications(false)}
                onAnnouncementClick={async (announcementId) => {
                  await handleMarkAnnouncementRead(announcementId);
                  setDeepLinkAnnouncementId(announcementId);
                  setActiveTab('announcements');
                }}
                onClaimClick={(claimId, status) => {
                  setClaimsInitialSubTab('claims');
                  setDeepLinkClaimId(claimId);
                  setActiveTab('claims');
                }}
                onDirectThreadClick={(thread) => {
                  setSelectedThread(thread);
                  setOpenMessagesModal(true);
                }}
              />
            )}
            <button
              onClick={() => setActiveTab('profile')}
              className="guest-home__avatar overflow-hidden"
              aria-label="Profile"
              data-testid="header-avatar"
            >
              <Avatar src={user?.photo_url} name={user?.name} iconClassName="w-5 h-5 text-foreground" initialClassName="font-heading font-bold text-sm" />
            </button>
          </div>
        </div>
      </header>

      {}
      <main className="max-w-lg mx-auto p-4 pb-24" style={{ paddingBottom: 'calc(5.5rem + env(safe-area-inset-bottom))' }}>
        {activeTab === 'home' && showWelcomeBanner && (
          <WelcomeBanner
            user={user}
            language={language}
            onCompleteProfile={completeProfile}
            onShowTutorial={() => setShowTutorial(true)}
            onDismiss={dismissWelcomeBanner}
          />
        )}

        {activeTab === 'home' && activeSurvey && (
          <div className="survey-invite survey-invite--legacy p-4 mb-4 flex items-center justify-between gap-3" data-testid="survey-invite-legacy">
            <div className="survey-invite__content min-w-0">
              <div className="survey-invite__heading">
                <span className="survey-invite__icon" aria-hidden="true">
                  <MessageSquare className="w-4 h-4" strokeWidth={2} />
                </span>
                <p className="survey-invite__eyebrow">{language === 'es' ? 'TU OPINIÓN NOS IMPORTA' : 'YOUR OPINION MATTERS'}</p>
              </div>
              <p className="survey-invite__title">{language === 'es' ? '¿Cómo está siendo tu experiencia en WannaColive?' : 'How is your experience at WannaColive?'}</p>
              <p className="survey-invite__description">{language === 'es' ? 'Nos ayuda a seguir mejorando.' : 'It helps us keep improving.'}</p>
            </div>
            <button onClick={() => setShowSurvey(true)} className="survey-invite__cta" data-testid="survey-start">
              {language === 'es' ? 'Responder encuesta' : 'Take survey'} <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {activeTab === 'home' && (
          <HomeTab 
            user={user}
            claims={claims}
            announcements={announcements}
            benefits={benefits}
            unreadAnnouncements={unreadAnnouncements}
            loading={loading}
            t={t}
            language={language}
            houses={houses}
            events={events}
            reservations={reservations}
            visits={visits}
            transfers={transfers}
            onNewClaim={() => setShowNewClaim(true)}
            onNewVisit={() => setShowNewVisit(true)}
            onNewTransfer={() => setShowNewTransfer(true)}
            onGoClaimsSubTab={goToClaimsSubTab}
            onMarkAnnouncementRead={handleMarkAnnouncementRead}
            onGoTab={setActiveTab}
            onCustomizeQA={() => setShowCustomizeQA(true)}
            onToggleEventAttendance={async (eventId) => {
              try {
                await api.toggleEventAttendance(eventId);
                await loadData({ silent: true });
              } catch (e) { console.error(e); }
            }}
            renewalEligibility={renewalElig}
            onRequestRenewal={() => setShowRenewal(true)}
            activeSurvey={activeSurvey}
            onOpenSurvey={() => setShowSurvey(true)}
            onOpenMessages={() => setOpenMessagesModal(true)}
          />
        )}

        {activeTab === 'claims' && (
          <ClaimsTab 
            claims={claims}
            loading={loading}
            t={t}
            language={language}
            onNewClaim={() => setShowNewClaim(true)}
            onCloseClaim={handleCloseClaim}
            currentUserId={user?.id}
            isExclusive={isExclusiveRoom}
            visits={visits}
            transfers={transfers}
            houses={houses}
            onNewVisit={() => setShowNewVisit(true)}
            onNewTransfer={() => setShowNewTransfer(true)}
            initialSubTab={claimsInitialSubTab}
            onSubTabChange={setClaimsInitialSubTab}
            autoOpenClaimId={deepLinkClaimId}
            onAutoOpened={() => setDeepLinkClaimId(null)}
            onReload={() => loadData({ silent: true })}
            onClaimRead={markClaimReadLocally}
          />
        )}

        {activeTab === 'announcements' && (
          <AnnouncementsTab
            announcements={announcements}
            loading={loading}
            language={language}
            userId={user?.id}
            onMarkRead={handleMarkAnnouncementRead}
            onBack={() => setActiveTab('home')}
            autoOpenId={deepLinkAnnouncementId}
            onAutoOpened={() => setDeepLinkAnnouncementId(null)}
            onDetailOpen={setAnnouncementDetailOpen}
          />
        )}

        {activeTab === 'reservations' && (
          <ReservationsTab user={user} t={t} language={language} theme={theme} />
        )}

        {activeTab === 'benefits' && (
          <BenefitsTab benefits={benefits} loading={loading} t={t} language={language} initialBenefitId={deepLinkBenefitId} onBenefitOpened={() => setDeepLinkBenefitId(null)} />
        )}

        {activeTab === 'community' && (
          <CommunityTab 
            publicProfiles={publicProfiles}
            loadProfiles={loadPublicProfiles}
            currentUserId={user?.id}
            currentUserHouseId={user?.house_id}
            currentUserSharesProfile={user?.share_profile}
            houses={houses}
            newGuests={newGuests}
            onEditProfile={() => { setActiveTab('profile'); setShowEditProfile(true); }}
            t={t}
            language={language}
          />
        )}

        {activeTab === 'services' && (
          <ServicesTab
            language={language}
            t={t}
            isExclusive={isExclusiveRoom}
            renewalEligible={renewalElig.eligible}
            counts={{ claims: getGuestClaimGroups(claims).active.length, visits: getGuestVisitGroups(visits).active.length, transfers: transfers.length, benefits: benefits.length, unread: unreadAnnouncements.length }}
            directThreadsUnread={directThreadsUnread}
            onNavigate={setActiveTab}
            onNavigateClaims={goToClaimsSubTab}
            onNewClaim={() => setShowNewClaim(true)}
            onNewVisit={() => setShowNewVisit(true)}
            onNewTransfer={() => setShowNewTransfer(true)}
            onRequestRenewal={() => setShowRenewal(true)}
            onOpenMessages={() => setOpenMessagesModal(true)}
          />
        )}

        {activeTab === 'profile' && (
          <ProfileTab 
            user={user} 
            logout={logout} 
            onEditProfile={() => setShowEditProfile(true)}
            t={t}
            language={language}
            theme={theme}
            onChangeTheme={toggleTheme}
            onChangeLanguage={toggleLanguage}
          />
        )}
      </main>

      {showWelcomeOnboarding && (
        <WelcomeOnboarding
          language={language}
          onClose={dismissWelcome}
          onCompleteProfile={completeProfile}
          onShowTutorial={showWelcomeTutorial}
          onGoTab={goToWelcomeTab}
        />
      )}

      {}
      {!openMessagesModal && !announcementDetailOpen && <nav className="guest-bottom-nav fixed bottom-0 left-0 right-0 ios-bottom-safe z-40">
        <div className="guest-bottom-nav__inner max-w-lg mx-auto">
          {navItems.map((tab) => {
            if (tab.isLogo) {
              return (
                <button
                  key="center-logo"
                  type="button"
                  onClick={() => setShowQuickActionsModal(true)}
                  className="guest-bottom-nav__item guest-bottom-nav__item--center"
                  data-testid="bottom-nav-logo"
                  aria-label={language === 'es' ? 'Accesos rápidos' : 'Quick actions'}
                >
                  <span className="guest-bottom-nav__icon-slot guest-bottom-nav__icon-slot--center">
                    <span className="guest-bottom-nav__center-pill nav-logo-pill">
                      <img
                        src="/logo192.png"
                        alt="WannaColive"
                        className="brand-logo-adaptive"
                        style={{ width: '24px', height: '24px', objectFit: 'contain', display: 'block' }}
                      />
                    </span>
                  </span>
                  <span className="guest-bottom-nav__label guest-bottom-nav__label--center">Wanna</span>
                </button>
              );
            }
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => tab.id === 'community' ? goToCommunity() : setActiveTab(tab.id)}
                className={`guest-bottom-nav__item ${isActive ? 'guest-bottom-nav__item--active' : ''}`}
                data-testid={`nav-${tab.id}`}
              >
                  <span className="guest-bottom-nav__icon-slot">
                    <Icon className="guest-bottom-nav__icon" strokeWidth={isActive ? 2.5 : 2} />
                  </span>
                <span className="guest-bottom-nav__label">{tab.label}</span>
                {tab.id === 'home' && unreadAnnouncements.length > 0 && (
                  <span className="guest-bottom-nav__badge">
                    {unreadAnnouncements.length}
                  </span>
                )}
                {tab.id === 'services' && directThreadsUnread > 0 && (
                  <span className="guest-bottom-nav__badge" data-testid="services-unread-badge">
                    {directThreadsUnread}
                  </span>
                )}
                {tab.id === 'community' && hasUnseenNewGuests && (
                  <span className="guest-bottom-nav__badge" data-testid="community-unread-badge" />
                )}
              </button>
            );
          })}
        </div>
      </nav>}

      {}
      {showNewClaim && <NewClaimModal user={user} onClose={() => setShowNewClaim(false)} onSubmit={handleCreateClaim} t={t} language={language} />}
      {showTutorial && (
        <TutorialModal
          language={language}
          onClose={() => setShowTutorial(false)}
          onGoTab={(tab) => { setShowTutorial(false); setActiveTab(tab); }}
        />
      )}
      {showSurvey && activeSurvey && (
        <SurveyFlow
          survey={activeSurvey}
          language={language}
          userName={user?.name}
          onClose={() => setShowSurvey(false)}
          onSubmit={async (id, payload) => {
            await api.submitSurveyResponse(id, payload);
          }}
        />
      )}
      {currentPopup && (
        <InfoPopupModal
          popup={currentPopup}
          language={language}
          onClose={handlePopupClose}
          onAction={handlePopupAction}
        />
      )}
      {showRenewal && (
        <ContractRenewalModal
          language={language}
          currentLeaseEnd={user?.lease_end}
          onClose={() => setShowRenewal(false)}
        />
      )}
      {showNewVisit && (
        <NewVisitModal
          onClose={() => setShowNewVisit(false)}
          onSubmit={handleCreateVisit}
          t={t}
          language={language}
          appSettings={appSettings}
          myHouse={houses?.find(h => h.id === user?.house_id) || null}
        />
      )}

      {openMessagesModal && createPortal(
        <div
          className="fixed inset-0 z-50 modal-backdrop messages-modal-flow-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedThread(null);
              setOpenMessagesModal(false);
            }
          }}
          data-testid="messages-modal-flow"
        >
          {selectedThread ? (
            <DirectChatModal
              thread={selectedThread}
              currentUserId={user?.id}
              viewerIsStaff={false}
              isAdminConversation={selectedThread.isAdminConversation === true}
              language={language}
              renderInParent
              onBack={() => setSelectedThread(null)}
              onClose={() => { setSelectedThread(null); setOpenMessagesModal(false); loadData({ silent: true }); }}
              onMessagesRead={() => loadData({ silent: true })}
            />
          ) : (
            <MessagesModalSelector
              directThreads={directThreads}
              user={user}
              language={language}
              embedded
              onClose={() => setOpenMessagesModal(false)}
              onSelectThread={(thread) => setSelectedThread(thread)}
            />
          )}
        </div>,
        document.body
      )}
      {showNewTransfer && <NewTransferModal onClose={() => setShowNewTransfer(false)} onSubmit={handleCreateTransfer} houses={houses} currentHouseId={user?.house_id} t={t} />}
      {showEditProfile && <EditProfileModal user={user} onClose={() => setShowEditProfile(false)} onSubmit={handleUpdateProfile} t={t} language={language} />}
      {showQuickActionsModal && (
        <QuickActionsSheet
          language={language}
          theme={theme}
          onClose={() => setShowQuickActionsModal(false)}
          actions={[
            { label: language === 'es' ? 'Nuevo reclamo' : 'New claim', icon: AlertTriangle, color: 'bg-accent-mint', onClick: () => { setShowQuickActionsModal(false); setShowNewClaim(true); } },
            isExclusiveRoom && { label: language === 'es' ? 'Registrar visita' : 'Register visit', icon: UserRoundPlus, color: 'bg-accent-lavender', onClick: () => { setShowQuickActionsModal(false); setShowNewVisit(true); } },
            { label: language === 'es' ? 'Pedir cambio' : 'Request transfer', icon: Plane, color: 'bg-accent-yellow', disabled: true, comingSoon: true, onClick: () => {} },
            { label: language === 'es' ? 'Reservar espacio' : 'Reserve space', icon: CalendarDays, color: 'bg-accent-yellow', onClick: () => { setShowQuickActionsModal(false); setActiveTab('reservations'); } },
          ].filter(Boolean)}
        />
      )}
      {showCustomizeQA && (
        <CustomizeQuickActions
          language={language}
          current={user?.quick_actions || []}
          onClose={() => setShowCustomizeQA(false)}
          onSaved={async () => { setShowCustomizeQA(false); await checkAuth(); }}
        />
      )}

      {showExitConfirm && (
        <div className="fixed inset-0 z-[60] modal-backdrop" data-testid="exit-confirm-modal">
          <div className="bg-surface border-2 border-foreground rounded-2xl w-[88%] max-w-sm modal-card mx-auto mt-[40vh] p-6">
            <h3 className="font-heading font-bold text-lg mb-2">
              {language === 'es' ? '¿Querés salir de la app?' : 'Exit the app?'}
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              {language === 'es'
                ? 'Podés volver cuando quieras tocando el ícono de WannaColive.'
                : 'You can come back anytime by tapping the WannaColive icon.'}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowExitConfirm(false)}
                className="flex-1 py-3 border-2 border-foreground rounded-lg font-medium hover:bg-muted transition-colors"
                data-testid="exit-cancel-button"
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => {
                  
                  
                  
                  
                  setShowExitConfirm(false);
                  setTimeout(() => {
                    window.history.go(-2);
                  }, 50);
                }}
                className="flex-1 py-3 rounded-lg font-semibold bg-foreground text-background hover:opacity-90 transition-opacity"
                data-testid="exit-confirm-button"
              >
                {language === 'es' ? 'Salir' : 'Exit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickActionsSheet({ actions, language, theme, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 modal-backdrop"
      style={{
        backgroundColor: theme === 'light' ? 'rgba(0, 0, 0, 0.11)' : 'rgba(0, 0, 0, 0.24)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="quick-actions-sheet"
    >
      <div className="bg-surface border-2 border-foreground rounded-2xl w-full max-w-md modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">{language === 'es' ? '¿Qué querés hacer?' : 'What do you want to do?'}</h3>
            <button onClick={onClose} className="p-1 text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {actions.map((a, i) => (
              <button
                key={i}
                onClick={a.disabled ? undefined : a.onClick}
                disabled={a.disabled}
                className={`card-brutal-static p-4 flex flex-col items-center gap-2 relative ${a.disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-muted'}`}
                data-testid={`qa-action-${i}`}
              >
                {a.comingSoon && (
                  <span className="absolute top-1 right-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-foreground text-background">
                    {language === 'es' ? 'Próximamente' : 'Coming soon'}
                  </span>
                )}
                <div className={`w-12 h-12 rounded-xl ${a.color} border-2 border-foreground flex items-center justify-center`}>
                  <a.icon className="w-5 h-5" />
                </div>
                <span className="text-xs font-semibold text-center leading-tight">{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomizeQuickActions({ current, language, onClose, onSaved }) {
  const all = [
    { key: 'new_claim', label: language === 'es' ? 'Nuevo reclamo' : 'New claim' },
    { key: 'new_visit', label: language === 'es' ? 'Solicitar visita' : 'Request visit' },
    
    { key: 'reserve', label: language === 'es' ? 'Reservar espacio' : 'Reserve space' },
    { key: 'announcements', label: language === 'es' ? 'Comunicados' : 'Announcements' },
    { key: 'benefits', label: language === 'es' ? 'Beneficios' : 'Benefits' },
    { key: 'messages', label: language === 'es' ? 'Mensajes' : 'Messages' },
  ];
  const [selected, setSelected] = useState(current.length ? current : ['new_claim', 'new_visit', 'reserve', 'benefits']);
  const [saving, setSaving] = useState(false);
  const toggle = (k) => {
    setSelected(prev => prev.includes(k) ? prev.filter(x => x !== k) : prev.length < 4 ? [...prev, k] : prev);
  };
  const save = async () => {
    setSaving(true);
    try { await api.setQuickActions(selected); onSaved && onSaved(); } catch (e) { console.error(e); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 modal-backdrop" data-testid="customize-qa-modal">
      <div className="bg-surface border-2 border-foreground rounded-xl w-full max-w-md modal-card">
        <div className="p-5">
          <h3 className="font-heading font-bold text-lg mb-1">{language === 'es' ? 'Personalizar accesos' : 'Customize quick actions'}</h3>
          <p className="text-xs text-muted-foreground mb-4">{language === 'es' ? 'Elegí hasta 4 atajos.' : 'Pick up to 4 shortcuts.'} ({selected.length}/4)</p>
          <div className="space-y-2">
            {all.map(opt => {
              const on = selected.includes(opt.key);
              return (
                <button key={opt.key} onClick={() => toggle(opt.key)} className={`w-full p-3 border-2 rounded-lg text-left flex items-center justify-between ${on ? 'border-foreground bg-primary text-background' : 'border-border hover:bg-muted text-foreground'}`} data-testid={`qa-option-${opt.key}`}>
                  <span className="font-semibold text-sm">{opt.label}</span>
                  {on && <Check className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={onClose} className="flex-1 py-3 border-2 border-foreground rounded-lg font-semibold">{language === 'es' ? 'Cancelar' : 'Cancel'}</button>
            <button onClick={save} disabled={saving} className="btn-brutal flex-1 py-3 disabled:opacity-50" data-testid="save-qa-btn">{saving ? '...' : (language === 'es' ? 'Guardar' : 'Save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HomeTab({ user, claims, announcements, benefits, unreadAnnouncements, loading, t, language, houses, events, reservations, visits, transfers, onNewClaim, onNewVisit, onNewTransfer, onGoClaimsSubTab, onMarkAnnouncementRead, onGoTab, onCustomizeQA, onToggleEventAttendance, renewalEligibility, onRequestRenewal, activeSurvey, onOpenSurvey, onOpenMessages }) {
  const myHouse = houses?.find(h => h.id === user?.house_id);
  
  
  
  
  const activeClaims = getGuestClaimGroups(claims).active;
  const activeClaim = activeClaims[0];
  const upcomingReservations = getUpcomingGuestReservations(reservations);
  const upcomingReservation = upcomingReservations[0];
  const upcomingVisit = (visits || [])
    .filter(v => v.status !== 'rejected' && v.visit_date >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => new Date(a.visit_date) - new Date(b.visit_date))[0];
  const nextEvent = (events || [])
    .filter(e => new Date(e.starts_at) > new Date())
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))[0];
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const benefitsCarouselRef = useRef(null);
  const [benefitsCarouselState, setBenefitsCarouselState] = useState({
    scrollable: false,
    thumbWidth: 100,
    thumbLeft: 0,
  });

  useEffect(() => {
    const carousel = benefitsCarouselRef.current;
    if (!carousel) return undefined;

    const syncCarouselState = () => {
      const maxScroll = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
      const scrollable = maxScroll > 1;
      const thumbWidth = scrollable
        ? Math.max(22, Math.min(100, (carousel.clientWidth / carousel.scrollWidth) * 100))
        : 100;
      const progress = scrollable ? Math.min(1, Math.max(0, carousel.scrollLeft / maxScroll)) : 0;
      const thumbLeft = progress * (100 - thumbWidth);

      setBenefitsCarouselState((current) => {
        if (
          current.scrollable === scrollable
          && Math.abs(current.thumbWidth - thumbWidth) < 0.1
          && Math.abs(current.thumbLeft - thumbLeft) < 0.1
        ) {
          return current;
        }
        return { scrollable, thumbWidth, thumbLeft };
      });
    };

    syncCarouselState();
    carousel.addEventListener('scroll', syncCarouselState, { passive: true });
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(syncCarouselState)
      : null;
    resizeObserver?.observe(carousel);
    window.addEventListener('resize', syncCarouselState);

    return () => {
      carousel.removeEventListener('scroll', syncCarouselState);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncCarouselState);
    };
  }, [benefits.length]);

  const isExclusive = user?.room_type === 'exclusive';
  const allActions = [
    { key: 'new_claim', label: language === 'es' ? 'Nuevo reclamo' : 'New claim', icon: AlertTriangle, color: 'bg-accent-mint', onClick: onNewClaim },
    isExclusive && { key: 'new_visit', label: language === 'es' ? 'Registrar visita' : 'Register visit', icon: UserRoundPlus, color: 'bg-accent-lavender', onClick: onNewVisit },
    
    { key: 'reserve', label: language === 'es' ? 'Reservar espacio' : 'Reserve space', icon: CalendarDays, color: 'bg-accent-yellow', onClick: () => onGoTab('reservations') },
    { key: 'announcements', label: language === 'es' ? 'Comunicados' : 'Announcements', icon: Bell, color: 'icon-pill-blue', onClick: () => onGoTab('announcements') },
    { key: 'benefits', label: language === 'es' ? 'Beneficios' : 'Benefits', icon: Gift, color: 'bg-accent-mint', onClick: () => onGoTab('benefits') },
    { key: 'messages', label: language === 'es' ? 'Mensajes' : 'Messages', icon: MessageSquare, color: 'icon-pill-blue', onClick: onOpenMessages },
  ].filter(Boolean);
  const defaultKeys = isExclusive ? ['new_claim', 'new_visit', 'reserve', 'benefits'] : ['new_claim', 'reserve', 'announcements', 'benefits'];
  const userQAKeys = (user?.quick_actions && user.quick_actions.length) ? user.quick_actions : defaultKeys;
  const userQA = userQAKeys.map(k => allActions.find(a => a.key === k)).filter(Boolean).slice(0, 4);

  return (
    <div className="space-y-5 animate-enter" data-testid="guest-home-tab">
      {}
      <div className="guest-home__residence-banner">
        {myHouse?.cover_photo ? (
          <img src={myHouse.cover_photo} alt={myHouse.name} className="guest-home__residence-image" />
        ) : (
          <div className="guest-home__residence-fallback" />
        )}
        <div className="guest-home__residence-overlay" />
        <div className="guest-home__residence-content">
          <div className="guest-home__residence-status-row">
            <span className={`guest-home__residence-status ${activeClaim ? 'guest-home__residence-status--attention' : 'guest-home__residence-status--clear'}`}>
              {activeClaim ? (language === 'es' ? '⚠ Reclamo activo' : '⚠ Active claim') : (language === 'es' ? '✓ Todo al día' : '✓ All up to date')}
            </span>
          </div>
          <div className="guest-home__residence-copy">
            <h2 className="guest-home__residence-title font-heading">{myHouse?.name || 'WannaColive'}</h2>
            <p className="guest-home__residence-city">{myHouse?.city || (language === 'es' ? 'Tu casa' : 'Your home')}</p>
          </div>
        </div>
      </div>

      {activeSurvey && (
        <div className="survey-invite survey-invite--home" data-testid="survey-invite">
          <span className="survey-invite__icon survey-invite__icon--home" aria-hidden="true">
            <BarChart3 className="w-5 h-5" strokeWidth={2} />
          </span>
          <div className="survey-invite__body">
            <div className="survey-invite__heading">
              <p className="survey-invite__eyebrow">{language === 'es' ? 'TU OPINIÓN NOS IMPORTA' : 'YOUR OPINION MATTERS'}</p>
            </div>
            <p className="survey-invite__title">{language === 'es' ? '¿Cómo está siendo tu experiencia en WannaColive?' : 'How is your experience at WannaColive?'}</p>
            <p className="survey-invite__raffle-note">{language === 'es' ? 'Participá del sorteo al responder' : 'Enter the raffle by responding'}</p>
            <button type="button" onClick={onOpenSurvey} className="survey-invite__cta" data-testid="survey-start">
              {language === 'es' ? 'Responder encuesta' : 'Take survey'} <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {}
      {renewalEligibility?.eligible && onRequestRenewal && (
        <button
          type="button"
          onClick={onRequestRenewal}
          className="card-brutal w-full p-4 text-left flex items-center gap-3 renewal-banner"
          data-testid="renewal-banner"
        >
          <span className="w-12 h-12 rounded-xl bg-foreground text-background flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-foreground">
              {language === 'es' ? 'Tu contrato vence pronto' : 'Your lease ends soon'}
            </p>
            <p className="text-xs text-foreground/80">
              {renewalEligibility.daysRemaining === 0
                ? (language === 'es' ? 'Vence hoy. Pedí la renovación.' : 'Ends today. Request renewal.')
                : renewalEligibility.daysRemaining === 1
                  ? (language === 'es' ? 'Falta 1 día. Pedí la renovación.' : '1 day left. Request renewal.')
                  : (language === 'es'
                    ? `Faltan ${renewalEligibility.daysRemaining} días. Pedí la renovación.`
                    : `${renewalEligibility.daysRemaining} days left. Request renewal.`)}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-foreground" />
        </button>
      )}

      {}
      <div className="guest-home__metrics">
        <button onClick={() => onGoClaimsSubTab('claims')} className="guest-home__metric-card guest-home__metric-card--claim" data-testid="kpi-claims">
          <div className="guest-home__metric-head">
            <span className="guest-home__metric-icon guest-home__metric-icon--claim">
              <AlertTriangle className="w-5 h-5" strokeWidth={2} />
            </span>
            <p className="guest-home__metric-label">{language === 'es' ? 'Reclamo' : 'Claim'}</p>
          </div>
          <p className="guest-home__metric-value font-heading">{activeClaims.length}</p>
          <p className="guest-home__metric-description">{activeClaims.length > 0
            ? (language === 'es' ? (activeClaims.length === 1 ? 'activo' : 'activos') : 'active')
            : (language === 'es' ? 'sin reclamos' : 'no claims')}</p>
        </button>
        <button onClick={() => onGoTab('reservations')} className="guest-home__metric-card guest-home__metric-card--reservation" data-testid="kpi-reservations">
          <div className="guest-home__metric-head">
            <span className="guest-home__metric-icon guest-home__metric-icon--reservation">
              <CalendarDays className="w-5 h-5" strokeWidth={2} />
            </span>
            <p className="guest-home__metric-label">{language === 'es' ? 'Reserva' : 'Reservation'}</p>
          </div>
          <p className="guest-home__metric-value font-heading">{upcomingReservations.length}</p>
          <p className="guest-home__metric-description">{upcomingReservations.length > 0
            ? (language === 'es' ? (upcomingReservations.length === 1 ? 'próxima' : 'próximas') : 'upcoming')
            : (language === 'es' ? 'sin reservas' : 'none')}</p>
        </button>
        <button onClick={() => onGoTab('announcements')} className="guest-home__metric-card guest-home__metric-card--unread" data-testid="kpi-unread-announcements">
          <div className="guest-home__metric-head">
            <span className="guest-home__metric-icon guest-home__metric-icon--unread">
              <Megaphone className="w-5 h-5" strokeWidth={2} />
            </span>
            <p className="guest-home__metric-label">{language === 'es' ? 'Sin leer' : 'Unread'}</p>
          </div>
          <p className="guest-home__metric-value font-heading">{unreadAnnouncements.length}</p>
          <p className="guest-home__metric-description">{language === 'es' ? 'comunicados' : 'announcements'}</p>
        </button>
      </div>

      {}
      <section className="guest-home__quick-actions" data-testid="guest-quick-actions">
        <div className="guest-home__section-heading">
          <h3 className="guest-home__section-title font-heading text-foreground">{language === 'es' ? 'Accesos rápidos' : 'Quick actions'}</h3>
          <button onClick={onCustomizeQA} className="guest-home__customize" data-testid="customize-qa-btn">
            <Edit2 className="w-3.5 h-3.5" />
            {language === 'es' ? 'Personalizar' : 'Customize'}
          </button>
        </div>
        <div className="guest-home__quick-grid">
          {userQA.map((a) => (
            <button key={a.key} onClick={a.onClick} className="guest-home__quick-card" data-testid={`qa-${a.key}`}>
              <div className={`guest-home__quick-icon ${a.color}`}>
                <a.icon className="w-5 h-5" strokeWidth={2} />
              </div>
              <span className="guest-home__quick-label">{a.label}</span>
            </button>
          ))}
        </div>
      </section>

      {}
      {nextEvent && (
        <EventCard event={nextEvent} language={language} userId={user?.id} onToggle={onToggleEventAttendance} />
      )}

      {}
      {upcomingReservation && (
        <section>
          <h3 className="font-heading font-semibold text-foreground mb-3">{language === 'es' ? 'Tu próxima reserva' : 'Your next reservation'}</h3>
          <button onClick={() => onGoTab('reservations')} className="guest-home__upcoming-card w-full p-4 text-left flex items-center gap-3" data-testid="upcoming-reservation-card">
            <div className="w-12 h-12 bg-accent-mint border-2 border-foreground rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
              {(() => {
                const SpaceIcon = getSpaceIcon(upcomingReservation.space_type);
                return <SpaceIcon className="w-5 h-5" strokeWidth={2} aria-hidden="true" />;
              })()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold truncate">{upcomingReservation.space_name || (language === 'es' ? 'Espacio reservado' : 'Reserved space')}</p>
              <p className="text-xs text-muted-foreground">{formatDateAR(upcomingReservation.date, language === 'es' ? 'es-AR' : 'en-US')} · {upcomingReservation.start_time}–{upcomingReservation.end_time}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </section>
      )}

      {}
      {upcomingVisit && (
        <section>
          <h3 className="font-heading font-semibold text-foreground mb-3">{language === 'es' ? 'Tu próxima visita' : 'Your next visit'}</h3>
          <button onClick={() => onGoClaimsSubTab('visits')} className="guest-home__upcoming-card w-full p-4 text-left flex items-center gap-3" data-testid="upcoming-visit-card">
            <div className="w-12 h-12 bg-accent-lavender border-2 border-foreground rounded-xl flex items-center justify-center flex-shrink-0">
              <UserRoundPlus className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold truncate">{upcomingVisit.visitor_name}</p>
              <p className="text-xs text-muted-foreground">
                {formatDateAR(upcomingVisit.visit_date, language === 'es' ? 'es-AR' : 'en-US')}
                {' · '}
                {(() => {
                  const raw = upcomingVisit.visit_duration || '';
                  const m = /^(\d+)\s*nights?$/i.exec(raw);
                  if (m) {
                    const n = parseInt(m[1], 10);
                    return language === 'es' ? (n === 1 ? '1 noche' : `${n} noches`) : raw;
                  }
                  if (raw === 'weekend') return language === 'es' ? 'Fin de semana' : 'Weekend';
                  if (raw === '1 week') return language === 'es' ? '1 semana' : '1 week';
                  return raw;
                })()}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </section>
      )}

      {}
      <section className="guest-home__announcements">
        <div className="guest-home__announcements-heading">
          <h3 className="guest-home__announcements-title font-heading text-foreground">{t('recentAnnouncements')}</h3>
          {announcements.length > 0 && (
            <button onClick={() => onGoTab('announcements')} className="guest-home__announcements-all" data-testid="view-all-announcements-btn">
              {language === 'es' ? 'Ver todos' : 'View all'}
            </button>
          )}
        </div>
        {loading ? (
          <LoadingIndicator label={t('loading')} className="guest-home__announcements-loading" />
        ) : announcements.length === 0 ? (
          <div className="guest-home__announcements-empty" data-testid="home-announcements-empty">
            <span className="guest-home__announcements-empty-icon" aria-hidden="true">
              <Megaphone className="w-5 h-5" />
            </span>
            <div className="guest-home__announcements-empty-copy">
              <p className="guest-home__announcements-empty-title font-heading">
                {language === 'es' ? 'Todo tranquilo por ahora' : 'All quiet for now'}
              </p>
              <p className="guest-home__announcements-empty-text">
                {language === 'es'
                  ? 'Te avisaremos cuando haya novedades en tu residencia.'
                  : 'We will let you know when there are updates at your residence.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="guest-home__announcements-list">
            {announcements.slice(0, 3).map((announcement) => (
              <AnnouncementCard key={announcement.id} announcement={announcement} userId={user?.id} onMarkRead={onMarkAnnouncementRead} language={language} />
            ))}
          </div>
        )}
      </section>

      {}
      {benefits.length > 0 && (
        <section className="guest-home__benefits">
          <h3 className="guest-home__benefits-title font-heading font-semibold text-foreground">
            {language === 'es' ? 'Beneficios destacados' : 'Featured benefits'}
          </h3>
          <div
            ref={benefitsCarouselRef}
            className="guest-home__benefits-carousel snap-x snap-mandatory"
            data-testid="home-benefits-carousel"
          >
            {benefits.map((benefit) => (
              <button
                key={benefit.id}
                type="button"
                onClick={() => setSelectedBenefit(benefit)}
                className="guest-home__benefit-card min-w-[220px] flex-shrink-0 overflow-hidden snap-start text-left"
                data-testid={`home-benefit-${benefit.id}`}
              >
                <div className={`guest-home__benefit-card-visual relative h-28 flex items-center justify-center overflow-hidden ${benefit.image_url ? 'bg-white' : 'bg-gradient-to-br from-accent-yellow to-accent-peach'}`}>
                  {benefit.image_url ? (
                    <img src={benefit.image_url} alt={benefit.title} className="guest-home__benefit-card-image absolute inset-0 w-full h-full object-contain" />
                  ) : benefit.business_logo ? (
                    <div className="guest-home__benefit-card-logo-frame w-14 h-14 rounded-xl border-2 border-foreground bg-white overflow-hidden flex items-center justify-center shadow-md">
                      <img src={benefit.business_logo} alt={benefit.business_name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <Gift className="w-10 h-10 text-foreground/20" />
                  )}
                  {benefit.discount && (
                    <span className="guest-home__benefit-card-discount absolute top-2 right-2 px-2 py-1 text-xs font-bold rounded-full">
                      {benefit.discount}
                    </span>
                  )}
                </div>
                <div className="p-3 relative">
                  {benefit.image_url && benefit.business_logo && (
                    <div className="guest-home__benefit-card-logo-frame absolute -top-6 left-3 w-11 h-11 rounded-xl">
                      <img src={benefit.business_logo} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className={benefit.image_url && benefit.business_logo ? "pt-4" : ""}>
                    <p className="font-heading font-semibold text-foreground text-sm truncate">{benefit.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{benefit.business_name}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {benefitsCarouselState.scrollable && (
            <div className="guest-home__benefits-scroll-track" data-testid="home-benefits-scroll-indicator" aria-hidden="true">
              <span
                className="guest-home__benefits-scroll-thumb"
                style={{
                  width: `${benefitsCarouselState.thumbWidth}%`,
                  left: `${benefitsCarouselState.thumbLeft}%`,
                }}
              />
            </div>
          )}
        </section>
      )}

      {selectedBenefit && (
        <BenefitDetailSheet
          benefit={selectedBenefit}
          language={language}
          onClose={() => setSelectedBenefit(null)}
        />
      )}
    </div>
  );
}

function EventCard({ event, language, userId, onToggle }) {
  const attending = (event.attendee_ids || []).includes(userId);
  const day = getDayAR(event.starts_at);
  const month = formatMonthShortAR(event.starts_at, language === 'es' ? 'es-AR' : 'en-US');
  return (
    <section data-testid="next-event-card">
      <h3 className="font-heading font-semibold text-foreground mb-3">{language === 'es' ? 'Próximo evento' : 'Next event'}</h3>
      <div className="card-brutal overflow-hidden">
        <div className="relative h-32 bg-gradient-to-br from-accent-lavender to-accent-mint">
          {event.image_url && (
            <img src={event.image_url} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute top-3 left-3 bg-background border-2 border-foreground rounded-xl p-2 text-center min-w-[56px]">
            <p className="text-2xl font-heading font-bold leading-none">{day}</p>
            <p className="text-[10px] font-bold text-muted-foreground">{month}</p>
          </div>
        </div>
        <div className="p-4">
          <p className="font-heading font-bold text-lg">{event.title}</p>
          {event.location && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</p>
          )}
          <div className="flex items-center justify-between mt-3 gap-3">
            <span className="text-xs text-muted-foreground">{(event.attendee_ids || []).length} {language === 'es' ? 'asistentes' : 'attending'}</span>
            <button
              onClick={() => onToggle && onToggle(event.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border-2 border-foreground ${attending ? 'bg-accent-mint' : 'bg-primary text-background'}`}
              data-testid="event-rsvp-btn"
            >
              {attending ? (language === 'es' ? '✓ Voy' : '✓ Going') : (language === 'es' ? 'Confirmar' : 'RSVP')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ClaimsTab({ claims, loading, t, language, onNewClaim, onCloseClaim, currentUserId, isExclusive, visits, transfers, houses, onNewVisit, onNewTransfer, initialSubTab, onSubTabChange, autoOpenClaimId, onAutoOpened, onReload, onClaimRead }) {
  const [subTab, setSubTab] = useState(initialSubTab || 'claims');
  const [showVisitHistory, setShowVisitHistory] = useState(false);
  const [showClosedClaims, setShowClosedClaims] = useState(false);

  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initialSubTab && initialSubTab !== subTab) {
      setSubTab(initialSubTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSubTab]);
  const labels = language === 'es' ? claimTypeLabels : claimTypeLabelsEn;
  const statuses = language === 'es' ? statusLabels : statusLabelsEn;

  
  
  
  
  const { active: activeClaims, closed: closedClaims } = getGuestClaimGroups(claims);
  const { active: activeVisits, historical: historicalVisits } = getGuestVisitGroups(visits);
  const activeTransfers = transfers.filter(t => t.status === 'pending');
  const claimsUnread = claims.reduce((acc, c) => acc + Number(c.unread_count || 0), 0);

  const subTabs = [
    { id: 'claims', label: language === 'es' ? 'Reclamos' : 'Claims', count: activeClaims.length, unread: claimsUnread, show: true },
    { id: 'visits', label: language === 'es' ? 'Visitas' : 'Visits', count: activeVisits.length, show: isExclusive },
    { id: 'transfers', label: language === 'es' ? 'Cambios' : 'Transfers', count: activeTransfers.length, show: true },
  ].filter(t => t.show);

  return (
    <div className="space-y-4 animate-enter" data-testid="guest-claims-tab">
      {}
      <div
        className={`grid gap-2 ${subTabs.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}
        data-testid="claims-subtabs"
      >
        {subTabs.map(st => (
          <button
            key={st.id}
            onClick={() => {
              setSubTab(st.id);
              onSubTabChange?.(st.id);
            }}
            className={`px-2 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all truncate border-2 ${
              subTab === st.id
                ? 'bg-foreground text-background border-foreground'
                : 'bg-surface text-foreground border-border hover:bg-muted'
            }`}
            data-testid={`claims-subtab-${st.id}`}
          >
            {st.label} ({st.count})
            {st.unread > 0 && (
              <span className="ml-1.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white" data-testid="claims-unread-badge">
                {st.unread}
              </span>
            )}
          </button>
        ))}
      </div>

      {subTab === 'claims' && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-lg text-foreground">{t('myClaims')}</h2>
            <button onClick={onNewClaim} className="btn-brutal px-4 py-2 text-sm flex items-center gap-2" data-testid="new-claim-button-tab">
              <Plus className="w-4 h-4" />
              {t('newClaim')}
            </button>
          </div>
          {loading ? (
            <LoadingIndicator label={t('loading')} className="py-6" />
          ) : activeClaims.length === 0 ? (
            <div className="card-brutal-static p-8 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">{claims.length === 0 ? t('noClaims') : (language === 'es' ? 'No tenés reclamos activos' : 'No active claims')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeClaims.map((claim) => <ClaimCard key={claim.id} claim={claim} language={language} showPhoto onClose={onCloseClaim} currentUserId={currentUserId} autoOpen={autoOpenClaimId === claim.id} onAutoOpened={onAutoOpened} onRead={onClaimRead} />)}
            </div>
          )}
          {closedClaims.length > 0 && (
            <section className="guest-history-section" data-testid="guest-closed-claims">
              <button
                type="button"
                className="guest-history-section__toggle"
                onClick={() => setShowClosedClaims(open => !open)}
                aria-expanded={showClosedClaims}
              >
                <span>{language === 'es' ? `Reclamos cerrados (${closedClaims.length})` : `Closed claims (${closedClaims.length})`}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showClosedClaims ? 'rotate-180' : ''}`} />
              </button>
              {showClosedClaims && (
                <div className="guest-history-section__content space-y-3">
                  {closedClaims.map((claim) => <ClaimCard key={claim.id} claim={claim} language={language} showPhoto muted onClose={onCloseClaim} currentUserId={currentUserId} onRead={onClaimRead} />)}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {subTab === 'visits' && isExclusive && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-lg text-foreground">{t('myVisits')}</h2>
            <button onClick={onNewVisit} className="btn-brutal px-4 py-2 text-sm flex items-center gap-2">
              <UserRoundPlus className="w-4 h-4" />
              {t('requestVisit')}
            </button>
          </div>
          {activeVisits.length === 0 ? (
            <div className="card-brutal-static p-8 text-center">
              <UserRoundPlus className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">{language === 'es' ? 'No tenés visitas registradas' : 'No visits registered'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeVisits.map((visit) => <VisitCard key={visit.id} visit={visit} language={language} onCancelled={() => onReload && onReload()} />)}
            </div>
          )}
          {historicalVisits.length > 0 && (
            <section className="guest-history-section" data-testid="guest-visit-history">
              <button
                type="button"
                className="guest-history-section__toggle"
                onClick={() => setShowVisitHistory(open => !open)}
                aria-expanded={showVisitHistory}
              >
                <span>{language === 'es' ? `Historial (${historicalVisits.length})` : `History (${historicalVisits.length})`}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showVisitHistory ? 'rotate-180' : ''}`} />
              </button>
              {showVisitHistory && (
                <div className="guest-history-section__content space-y-3">
                  {historicalVisits.map((visit) => <VisitCard key={visit.id} visit={visit} language={language} muted onCancelled={() => onReload && onReload()} />)}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {subTab === 'transfers' && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-lg text-foreground">{t('myTransfers')}</h2>
            <span className="px-3 py-1.5 text-xs font-semibold border-2 border-foreground rounded-full bg-muted text-muted-foreground">
              {language === 'es' ? 'Próximamente' : 'Coming soon'}
            </span>
          </div>
          <div className="card-brutal-static p-8 text-center">
            <Plane className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-2 font-medium">{language === 'es' ? 'Función disponible próximamente' : 'Feature coming soon'}</p>
            <p className="text-xs text-muted-foreground">{language === 'es' ? 'Por ahora, contactá al equipo de WannaColive si necesitás cambiar de casa.' : 'For now, contact the WannaColive team if you need to change houses.'}</p>
          </div>
        </>
      )}
    </div>
  );
}

function BenefitsTab({ benefits, loading, t, language, initialBenefitId, onBenefitOpened }) {
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!initialBenefitId || !benefits?.length) return;
    const target = benefits.find((b) => String(b.id) === String(initialBenefitId));
    if (target) {
      setSelected(target);
      if (onBenefitOpened) onBenefitOpened();
    }
  }, [initialBenefitId, benefits, onBenefitOpened]);

  
  
  
  
  const formatDiscount = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    const bareDiscount = /^[\$€£]?\s?[\d.,]+\s?%?$/i.test(s);
    return bareDiscount ? `${s} OFF` : s;
  };

  const cats = [
    { id: 'all', label: language === 'es' ? 'Todos' : 'All' },
    { id: 'food', label: language === 'es' ? 'Comida' : 'Food' },
    { id: 'fitness', label: language === 'es' ? 'Deporte' : 'Fitness' },
    { id: 'study', label: language === 'es' ? 'Estudio' : 'Study' },
    { id: 'wellness', label: language === 'es' ? 'Bienestar' : 'Wellness' },
  ];
  const filtered = category === 'all' ? benefits : benefits.filter(b => (b.category || 'other') === category);

  return (
    <div className="space-y-4 animate-enter pb-4" data-testid="guest-benefits-tab">
      <div>
        <h2 className="font-heading font-bold text-2xl text-foreground">{language === 'es' ? 'Beneficios' : 'Benefits'}</h2>
        <p className="text-sm text-muted-foreground mt-1">{language === 'es' ? 'Descuentos exclusivos para colivers' : 'Exclusive discounts for colivers'}</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4" data-testid="benefits-categories">
        {cats.map(c => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors ${
              category === c.id
                ? 'bg-foreground text-background border-foreground'
                : 'bg-surface text-foreground border-border hover:bg-muted'
            }`}
            data-testid={`benefits-cat-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingIndicator label={t('loading')} className="py-6" />
      ) : filtered.length === 0 ? (
        <div className="card-brutal-static p-8 text-center">
          <Gift className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">{t('noBenefits')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(benefit => (
            <button
              key={benefit.id}
              onClick={() => setSelected(benefit)}
              className="card-brutal text-left w-full overflow-hidden block"
              data-testid={`benefit-${benefit.id}`}
            >
              {benefit.image_url || benefit.business_logo ? (
                <div className={`relative w-full h-32 overflow-hidden border-b-2 border-foreground flex items-center justify-center ${benefit.image_url ? 'bg-white' : 'bg-gradient-to-br from-accent-yellow to-accent-peach'}`}>
                  {benefit.image_url ? (
                    <img src={benefit.image_url} alt={benefit.title} className="w-full h-full object-contain" loading="lazy" data-testid={`benefit-image-${benefit.id}`} />
                  ) : (
                    <div className="guest-home__benefit-card-logo-frame w-16 h-16 rounded-xl">
                      <img src={benefit.business_logo} alt={benefit.business_name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  {benefit.discount && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-full bg-foreground text-background text-sm font-bold">
                      {formatDiscount(benefit.discount)}
                    </div>
                  )}
                </div>
              ) : (
                benefit.discount && (
                  <div className="px-4 pt-4">
                    <span className="inline-block px-3 py-1 rounded-full bg-foreground text-background text-sm font-bold">
                      {formatDiscount(benefit.discount)}
                    </span>
                  </div>
                )
              )}
              <div className="p-4 relative">
                {benefit.image_url && benefit.business_logo && (
                  <div className="guest-home__benefit-card-logo-frame absolute -top-7 left-4 w-12 h-12 rounded-lg">
                    <img src={benefit.business_logo} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className={benefit.image_url && benefit.business_logo ? "pt-5" : ""}>
                <h3 className="font-heading font-bold text-foreground">{benefit.business_name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{benefit.title}</p>
                {benefit.description && (
                  <p className="text-xs text-foreground mt-1.5 line-clamp-3 whitespace-pre-line">{benefit.description}</p>
                )}
                {(benefit.business_address || benefit.branches) && (
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{benefit.business_address || benefit.branches}</span>
                  </p>
                )}
                {benefit.payment_methods && benefit.payment_methods.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2" data-testid={`benefit-card-pm-${benefit.id}`}>
                    {benefit.payment_methods.map((m) => {
                      const labels = { cash: 'Efectivo', debit: 'Débito', credit: 'Crédito', mp: 'Mercado Pago', transfer: 'Transferencia' };
                      return (
                        <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-foreground border border-border font-medium">
                          {labels[m] || m}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <BenefitDetailSheet
          benefit={selected}
          language={language}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function BenefitDetailSheet({ benefit, language, onClose }) {
  const [redeem, setRedeem] = useState(null);
  const [dragY, setDragY] = useState(0);
  const dragRef = useRef({ active: false, startY: 0, offset: 0 });
  const mapsUrl = benefit.google_maps_url || benefit.maps_url || benefit.location_url || '';

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;
    const previous = {
      htmlOverscroll: html.style.overscrollBehavior,
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
    };

    html.style.overscrollBehavior = 'none';
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    return () => {
      html.style.overscrollBehavior = previous.htmlOverscroll;
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleSheetPointerDown = (event) => {
    dragRef.current = { active: true, startY: event.clientY, offset: 0 };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const handleSheetPointerMove = (event) => {
    if (!dragRef.current.active) return;
    const offset = Math.max(0, event.clientY - dragRef.current.startY);
    dragRef.current.offset = offset;
    setDragY(offset);
  };
  const handleSheetPointerEnd = () => {
    if (!dragRef.current.active) return;
    const shouldClose = dragRef.current.offset > 88;
    dragRef.current.active = false;
    if (shouldClose) onClose();
    else setDragY(0);
  };
  
  
  
  
  const formatDiscount = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    const bareDiscount = /^[\$€£]?\s?[\d.,]+\s?%?$/i.test(s);
    return bareDiscount ? `${s} OFF` : s;
  };
  return createPortal(
    <>
      <div
        className="benefit-detail-sheet fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="benefit-detail-sheet"
      >
        <div
          className="benefit-detail-sheet__panel bg-surface w-full max-w-md overflow-y-auto"
          style={{
            maxHeight: 'calc(92dvh - env(safe-area-inset-top, 0px))',
            transform: `translateY(${dragY}px)`,
            transition: dragRef.current.active ? 'none' : 'transform 180ms ease-out',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="benefit-detail-sheet__handle-zone"
            onPointerDown={handleSheetPointerDown}
            onPointerMove={handleSheetPointerMove}
            onPointerUp={handleSheetPointerEnd}
            onPointerCancel={handleSheetPointerEnd}
            aria-label={language === 'es' ? 'Deslizar para cerrar' : 'Swipe to close'}
            data-testid="benefit-detail-handle"
          >
            <span className="benefit-detail-sheet__handle" />
          </div>
          <div className="benefit-detail-sheet__content">
            <div className="benefit-detail-sheet__hero">
              {benefit.image_url ? (
                <img src={benefit.image_url} alt={benefit.title || benefit.business_name} className="benefit-detail-sheet__hero-image" />
              ) : (
                <div className="benefit-detail-sheet__hero-fallback"><Gift /></div>
              )}
              <div className="benefit-detail-sheet__hero-overlay" />
              <button onClick={onClose} className="benefit-detail-sheet__close" aria-label="Close" data-testid="benefit-detail-close">
                <X className="w-5 h-5" />
              </button>
              <div className="benefit-detail-sheet__hero-copy">
                <div className="benefit-detail-sheet__identity-icon">
                  {benefit.business_logo ? (
                    <img src={benefit.business_logo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Gift className="w-6 h-6" />
                  )}
                </div>
                <div className="min-w-0">
                  <h2 className="benefit-detail-sheet__business">{benefit.business_name}</h2>
                  {benefit.title && <p className="benefit-detail-sheet__subtitle">{benefit.title}</p>}
                </div>
              </div>
            </div>

            {benefit.discount && (
              <div className="benefit-detail-sheet__discount">
                <div className="benefit-detail-sheet__discount-icon">
                  <Gift className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="benefit-detail-sheet__eyebrow">{language === 'es' ? 'Beneficio disponible' : 'Benefit available'}</p>
                  <p className="benefit-detail-sheet__discount-value">{formatDiscount(benefit.discount)}</p>
                </div>
              </div>
            )}

            {}
            {benefit.description && (
              <div className="benefit-detail-sheet__section" data-testid="benefit-detail-description">
                <p className="benefit-detail-sheet__section-label">{language === 'es' ? 'Detalle' : 'Details'}</p>
                <p className="benefit-detail-sheet__body whitespace-pre-line">
                  {benefit.description}
                </p>
              </div>
            )}

            {(benefit.branches || benefit.business_address) && (
              <div className="benefit-detail-sheet__meta-row">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <div>
                  <span>{benefit.branches || benefit.business_address}</span>
                  {mapsUrl && (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="benefit-detail-sheet__maps-link"
                    >
                      {language === 'es' ? 'Ver en Google Maps' : 'View on Google Maps'}
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3 text-sm">
              {benefit.valid_days && (
                <div className="flex items-center gap-3 text-foreground">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span>{benefit.valid_days}</span>
                </div>
              )}
              {benefit.valid_until && (
                <div className="flex items-center gap-3 text-foreground">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {language === 'es' ? 'Vence el' : 'Expires'}{' '}
                    {formatDateAR(benefit.valid_until, language === 'es' ? 'es-AR' : 'en-US') || String(benefit.valid_until).slice(0, 10)}
                  </span>
                </div>
              )}
              {benefit.uses_per_user != null && benefit.uses_per_user > 0 && (
                <div className="flex items-center gap-3 text-foreground">
                  <User className="w-4 h-4 flex-shrink-0" />
                  <span>{benefit.uses_per_user} {language === 'es' ? `uso${benefit.uses_per_user === 1 ? '' : 's'} por coliver` : `use${benefit.uses_per_user === 1 ? '' : 's'} per coliver`}</span>
                </div>
              )}
              {benefit.payment_methods && benefit.payment_methods.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {benefit.payment_methods.map((m) => {
                    const labels = { cash: 'Efectivo', debit: 'Débito', credit: 'Crédito', mp: 'Mercado Pago', transfer: 'Transferencia' };
                    return (
                      <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-foreground border border-border font-medium">
                        {labels[m] || m}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="benefit-detail-sheet__how-it-works">
              <h3 className="benefit-detail-sheet__section-title">{language === 'es' ? 'Cómo funciona' : 'How it works'}</h3>
              <p className="benefit-detail-sheet__body">
                {benefit.how_it_works
                  || (language === 'es'
                    ? 'Generá tu código y mostralo en el local. El personal lo escanea y aplica tu descuento al instante.'
                    : 'Generate your code and show it at the venue. The staff will scan it to apply your discount instantly.')}
              </p>
            </div>
          </div>

          <div className="benefit-detail-sheet__footer">
            <button
              onClick={() => setRedeem(benefit)}
              className="benefit-detail-sheet__redeem w-full font-heading font-bold text-base hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              data-testid="benefit-redeem-button"
            >
              <Gift className="w-5 h-5" />
              {language === 'es' ? 'Canjear beneficio' : 'Redeem benefit'}
            </button>

            {benefit.more_info_url && (
              <a
                href={benefit.more_info_url}
                target="_blank"
                rel="noopener noreferrer"
                className="benefit-detail-sheet__more-info block text-center w-full font-heading font-semibold hover:bg-muted transition-colors"
                data-testid="benefit-more-info"
              >
                {language === 'es' ? 'Ver más info' : 'More info'}
              </a>
            )}
          </div>
        </div>
      </div>

      {redeem && (
        <BenefitRedeemQR
          benefit={redeem}
          language={language}
          onClose={() => setRedeem(null)}
        />
      )}
    </>,
    document.body
  );
}

function BenefitRedeemQR({ benefit, language, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(300);

  useEffect(() => {
    let mounted = true;
    api.benefitRedeemToken(benefit.id)
      .then(r => { if (mounted) { setData(r); setSecondsLeft(r.expires_in || 300); } })
      .catch(e => { if (mounted) setError(e.response?.data?.detail || 'Error generando código'); });
    return () => { mounted = false; };
  }, [benefit.id]);

  useEffect(() => {
    if (!data) return undefined;
    const id = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [data]);

  const expired = secondsLeft <= 0;
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <div className="benefit-redeem-modal fixed inset-0 z-[110] modal-backdrop flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="benefit-redeem-qr-modal">
      <div className="benefit-redeem-modal__panel bg-surface w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="benefit-redeem-modal__content">
          <div className="benefit-redeem-modal__header">
            <h3 className="benefit-redeem-modal__title">{language === 'es' ? 'Canjear beneficio' : 'Redeem benefit'}</h3>
            <button onClick={onClose} className="benefit-redeem-modal__close" aria-label="Close" data-testid="benefit-redeem-close"><X className="w-5 h-5" /></button>
          </div>

          {error && (
            <div className="benefit-redeem-modal__error" data-testid="benefit-redeem-error">
              {error}
            </div>
          )}

          {!data && !error && (
            <div className="text-center py-8 text-muted-foreground">{language === 'es' ? 'Generando código...' : 'Generating code...'}</div>
          )}

          {data && (
            <>
              <p className="benefit-redeem-modal__description">
                {language === 'es'
                  ? 'Mostrale este QR al personal del local. Lo escanea y verifica tu identidad y beneficio al instante.'
                  : 'Show this QR to the venue staff. They scan it and instantly verify your identity and benefit.'}
              </p>

              <div className={`benefit-redeem-modal__qr ${expired ? 'is-expired' : ''}`}>
                <QRCodeSVG
                  value={data.verify_url}
                  size={220}
                  level="M"
                  data-testid="benefit-redeem-qr-svg"
                />
              </div>

              <div className={`benefit-redeem-modal__timer ${expired ? 'is-expired' : ''}`} data-testid="benefit-redeem-timer">
                {expired
                  ? (language === 'es' ? 'Código expirado' : 'Code expired')
                  : `${language === 'es' ? 'Expira en' : 'Expires in'} ${String(mins).padStart(2,'0')}:${String(secs).padStart(2,'0')}`}
              </div>

              <div className="benefit-redeem-modal__manual-link">
                <p>{language === 'es' ? 'Si el local no puede escanear, abrí el link manualmente:' : 'If they cannot scan, open the link manually:'}</p>
                <a href={data.verify_url} target="_blank" rel="noopener noreferrer" data-testid="benefit-redeem-link">
                  {data.verify_url}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CommunityTab({ publicProfiles, loadProfiles, currentUserId, currentUserHouseId, currentUserSharesProfile, houses, newGuests = [], onEditProfile, t, language }) {
  const [loaded, setLoaded] = useState(false);
  const [scope, setScope] = useState('all'); 
  const [viewing, setViewing] = useState(null);
  const isNewProfile = (profile) => {
    const createdAt = Date.parse(profile?.created_at);
    return Number.isFinite(createdAt) && createdAt >= Date.now() - (7 * 24 * 60 * 60 * 1000);
  };

  useEffect(() => {
    if (!loaded) {
      loadProfiles();
      setLoaded(true);
    }
  }, [loaded, loadProfiles]);

  const otherProfiles = publicProfiles.filter(p => p.id !== currentUserId);
  const filteredProfiles = scope === 'myhouse' && currentUserHouseId
    ? otherProfiles.filter(p => p.house_id === currentUserHouseId)
    : otherProfiles;

  return (
    <div className="space-y-4 animate-enter" data-testid="guest-community-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-foreground">Comunidad</h2>
        <span className="text-xs text-muted-foreground">{filteredProfiles.length} {filteredProfiles.length === 1 ? 'persona' : 'personas'}</span>
      </div>

      {newGuests.length > 0 && (
        <section className="community-new-guests" data-testid="community-new-guests-banner">
          <div className="community-new-guests__header">
            <h3 className="community-new-guests__title">
              {language === 'es' ? 'Nuevos en la comunidad' : 'New in the community'}
            </h3>
          </div>

          <div className="community-new-guests__scroller" role="list">
            {newGuests.map((g) => {
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setViewing(g)}
                  className="community-new-guests__person"
                  data-testid={`new-guest-${g.id}`}
                  role="listitem"
                >
                  <span className="community-new-guests__avatar">
                    <Avatar src={g.photo_url} name={g.name} iconClassName="w-5 h-5 text-muted-foreground" initialClassName="font-heading font-bold text-base" />
                  </span>
                  <span className="community-new-guests__name">{g.name}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {!currentUserSharesProfile && (
        <div className="card-brutal-static p-4 flex items-start gap-3" data-testid="share-profile-hint">
          <LockKeyhole className="w-5 h-5 text-foreground flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">Tu perfil está privado</p>
            <p className="text-xs text-muted-foreground mt-1">Activá &ldquo;Compartir perfil&rdquo; en tu perfil para aparecer en la red y conocer a otros huéspedes.</p>
            <button
              onClick={onEditProfile}
              className="text-xs mt-2 px-3 py-1.5 border-2 border-foreground rounded-lg bg-foreground text-background hover:opacity-90 inline-flex items-center gap-1"
              data-testid="go-to-profile-privacy"
            >
              <Edit2 className="w-3 h-3" /> Editar perfil
            </button>
          </div>
        </div>
      )}

      {currentUserHouseId && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setScope('all')}
            className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${scope === 'all' ? 'bg-foreground text-background border-foreground font-semibold' : 'bg-surface text-foreground border-border hover:bg-muted'}`}
            data-testid="community-scope-all"
          >
            Todas las casas
          </button>
          <button
            onClick={() => setScope('myhouse')}
            className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${scope === 'myhouse' ? 'bg-foreground text-background border-foreground font-semibold' : 'bg-surface text-foreground border-border hover:bg-muted'}`}
            data-testid="community-scope-myhouse"
          >
            Mi casa
          </button>
        </div>
      )}

      {filteredProfiles.length === 0 ? (
        <div className="card-brutal-static p-8 text-center">
          <UsersRound className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-1">Todavía no hay huéspedes compartiendo perfil</p>
          <p className="text-xs text-muted-foreground">Invitá a tus compañeros a activar su perfil público.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredProfiles.map((profile) => {
            const house = houses.find(h => h.id === profile.house_id);
            return (
              <button
                key={profile.id}
                type="button"
                onClick={() => setViewing(profile)}
                className="card-brutal community-profile-card p-4 flex items-center gap-4 text-left w-full"
                data-testid={`community-profile-${profile.id}`}
              >
                <div className="w-14 h-14 rounded-full bg-muted border-2 border-border overflow-hidden flex-shrink-0">
                  <Avatar src={profile.photo_url} name={profile.name} iconClassName="w-6 h-6 text-muted-foreground" initialClassName="font-heading font-bold text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-foreground line-clamp-2 break-words">{profile.name}</h3>
                    {profile.nationality && (
                      <span className="text-lg">{getCountryFlag(profile.nationality)}</span>
                    )}
                    {isNewProfile(profile) && (
                      <span className="community-profile__new-badge">Nuevo</span>
                    )}
                  </div>
                  {house && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> {house.name}
                    </p>
                  )}
                  <p
                    className="community-profile-card__university text-sm text-muted-foreground truncate"
                    aria-hidden={!profile.university}
                  >
                    {profile.university || '\u00a0'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {viewing && (
        <CoLiverProfileSheet
          profile={viewing}
          house={houses.find(h => h.id === viewing.house_id)}
          language={language}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function WelcomeOnboarding({ language, onClose, onCompleteProfile, onShowTutorial, onGoTab }) {
  const isSpanish = language === 'es';
  const benefits = [
    {
      key: 'community',
      icon: UsersRound,
      title: isSpanish ? 'Comunidad' : 'Community',
      description: isSpanish ? 'Conectate con la comunidad' : 'Connect with the community',
    },
    {
      key: 'reservations',
      icon: CalendarDays,
      title: isSpanish ? 'Reservas' : 'Bookings',
      description: isSpanish ? 'Espacios a tu alcance' : 'Spaces within reach',
    },
    {
      key: 'benefits',
      icon: Gift,
      title: isSpanish ? 'Beneficios' : 'Benefits',
      description: isSpanish ? 'Descuentos y más' : 'Discounts and more',
    },
  ];

  return (
    <div className="welcome-onboarding" data-testid="welcome-onboarding">
      <div className="welcome-onboarding__backdrop" aria-hidden="true" />
      <div className="welcome-onboarding__viewport">
        <section className="welcome-onboarding__panel" role="dialog" aria-modal="true" aria-labelledby="welcome-onboarding-title">
          <button
            type="button"
            onClick={onClose}
            className="welcome-onboarding__close"
            aria-label={isSpanish ? 'Cerrar bienvenida' : 'Close welcome'}
            data-testid="welcome-onboarding-close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="welcome-onboarding__hero" aria-hidden="true">
            <img src="/sala-compartida-atardecer.png" alt="" className="welcome-onboarding__hero-image" />
          </div>

          <div className="welcome-onboarding__content">
            <p className="welcome-onboarding__eyebrow">
              {isSpanish ? 'TU NUEVA COMUNIDAD' : 'YOUR NEW COMMUNITY'}
            </p>
            <h1 id="welcome-onboarding-title" className="welcome-onboarding__title">
              {isSpanish ? '¡Bienvenido/a a WannaColive!' : 'Welcome to WannaColive!'}
            </h1>
            <p className="welcome-onboarding__intro">
              {isSpanish
                ? 'Todo lo que necesitás para gestionar tu día a día en la casa.'
                : 'Everything you need to feel at home, connected and up to date.'}
            </p>

            <div className="welcome-onboarding__benefits" aria-label={isSpanish ? 'Beneficios de WannaColive' : 'WannaColive benefits'}>
              {benefits.map(({ key, icon: Icon, title, description }) => (
                <button
                  type="button"
                  key={key}
                  className={`welcome-onboarding__benefit welcome-onboarding__benefit--${key}`}
                  onClick={() => onGoTab(key)}
                  data-testid={`welcome-onboarding-${key}`}
                >
                  <span className="welcome-onboarding__benefit-icon">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="welcome-onboarding__benefit-copy">
                    <span className="welcome-onboarding__benefit-title">{title}</span>
                    <span className="welcome-onboarding__benefit-description">{description}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="welcome-onboarding__cta-intro">
              <p className="welcome-onboarding__cta-lead">
                {isSpanish ? 'Primero queremos conocerte un poco mejor.' : 'First, we would like to get to know you a little better.'}
              </p>
              <p className="welcome-onboarding__cta-support">
                {isSpanish
                  ? 'Completá tu perfil y descubrí todo lo que podés hacer en WannaColive.'
                  : 'Complete your profile and discover everything you can do in WannaColive.'}
              </p>
            </div>

            <div className="welcome-onboarding__actions">
              <button
                type="button"
                onClick={onCompleteProfile}
                className="welcome-onboarding__primary"
                data-testid="welcome-onboarding-complete-profile"
              >
                {isSpanish ? 'Completar mi perfil' : 'Complete my profile'}
              </button>
              <button
                type="button"
                onClick={onShowTutorial}
                className="welcome-onboarding__secondary"
                data-testid="welcome-onboarding-tutorial"
              >
                {isSpanish ? 'Ver cómo funciona' : 'How it works'}
              </button>
              </div>
            </div>
        </section>
      </div>
    </div>
  );
}

function WelcomeBanner({ user, language, onCompleteProfile, onShowTutorial, onDismiss }) {
  const firstName = (user?.name || '').split(' ')[0];
  return (
    <div className="welcome-banner card-brutal p-4 mb-4 border-accent-yellow bg-accent-yellow/10 relative" data-testid="welcome-banner">
      <button onClick={onDismiss} className="absolute top-2 right-2 p-1 text-foreground" aria-label="Close" data-testid="welcome-banner-dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2 pr-6">
        <h2 className="font-heading font-bold text-lg text-foreground">
          {language === 'es' ? `¡Bienvenido/a a WannaColive${firstName ? ', ' + firstName : ''}!` : `Welcome to WannaColive${firstName ? ', ' + firstName : ''}!`}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        {language === 'es' ? 'Completá tu perfil y descubrí todo lo que podés hacer en WannaColive.' : 'Complete your profile and discover everything you can do in WannaColive.'}
      </p>

      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={onCompleteProfile} className="px-3 py-2 border-2 border-foreground rounded-lg bg-foreground text-background text-sm font-semibold hover:opacity-90" data-testid="welcome-complete-profile">
          {language === 'es' ? 'Completar mi perfil' : 'Complete my profile'}
        </button>
        <button onClick={onShowTutorial} className="px-3 py-2 border border-foreground rounded-lg bg-surface text-foreground text-sm font-semibold hover:bg-muted" data-testid="welcome-tutorial">
          {language === 'es' ? 'Ver cómo funciona' : 'How it works'}
        </button>
      </div>

    </div>
  );
}

function TutorialModal({ language, onClose }) {
  const [stepIndex, setStepIndex] = useState(0);
  const steps = [
    { icon: House, tone: 'neutral', title: language === 'es' ? 'Conocé WannaColive' : 'Meet WannaColive', desc: language === 'es' ? 'Todo lo que necesitás para vivir y disfrutar tu casa, desde un solo lugar.' : 'Everything you need to live and enjoy your home, all in one place.' },
    { icon: UsersRound, tone: 'violet', title: language === 'es' ? 'Comunidad' : 'Community', desc: language === 'es' ? 'Conocé a otros huéspedes, explorá perfiles y conectá con tu comunidad.' : 'Meet other guests, explore profiles and connect with your community.' },
    { icon: LayoutGrid, tone: 'blue', title: language === 'es' ? 'Servicios' : 'Services', desc: language === 'es' ? 'Reservá espacios, hacé reclamos, avisá visitas y contactá a la administración.' : 'Book spaces, submit claims, notify visits and contact administration.' },
    { icon: Gift, tone: 'blue', title: language === 'es' ? 'Beneficios' : 'Benefits', desc: language === 'es' ? 'Descubrí descuentos, promociones y beneficios exclusivos para vos.' : 'Discover discounts, promotions and exclusive benefits for you.' },
    { icon: UserRound, tone: 'neutral', title: language === 'es' ? 'Perfil' : 'Profile', desc: language === 'es' ? 'Actualizá tus datos, consultá la información de tu cuenta y encontrá respuestas cuando las necesites.' : 'Update your details, check your account information and find answers when you need them.' },
  ];
  const step = steps[stepIndex];
  const StepIcon = step.icon;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  return createPortal(
    <div className="welcome-tutorial" data-testid="tutorial-modal">
      <div className="welcome-tutorial__backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} aria-hidden="true" />
      <section className="welcome-tutorial__panel" role="dialog" aria-modal="true" aria-labelledby="welcome-tutorial-title">
        <div className="welcome-tutorial__visual">
          <div className={`welcome-tutorial__icon welcome-tutorial__icon--${step.tone}`}>
            <StepIcon className="w-8 h-8" strokeWidth={1.8} />
          </div>
          <span className="welcome-tutorial__progress">{stepIndex + 1} {language === 'es' ? 'de' : 'of'} {steps.length}</span>
          <button type="button" onClick={onClose} className="welcome-tutorial__close" aria-label={language === 'es' ? 'Cerrar tutorial' : 'Close tutorial'} data-testid="tutorial-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="welcome-tutorial__content">
          <p
            className={`welcome-tutorial__eyebrow${stepIndex === 0 ? '' : ' welcome-tutorial__eyebrow--placeholder'}`}
            aria-hidden={stepIndex !== 0}
          >
            {stepIndex === 0 && (language === 'es' ? 'CÓMO FUNCIONA WANNACOLIVE' : 'HOW WANNACOLIVE WORKS')}
          </p>
          <h3 id="welcome-tutorial-title" className="welcome-tutorial__title">{step.title}</h3>
          <p className="welcome-tutorial__description">{step.desc}</p>
          <div className="welcome-tutorial__dots" aria-hidden="true">
            {steps.map((_, i) => <span key={i} className={`welcome-tutorial__dot${i === stepIndex ? ' is-active' : ''}`} />)}
          </div>
        </div>
        <div className="welcome-tutorial__actions">
          <button type="button" onClick={() => setStepIndex((current) => Math.max(0, current - 1))} className="welcome-tutorial__previous" disabled={isFirst} data-testid="tutorial-previous">
            {language === 'es' ? 'Anterior' : 'Back'}
          </button>
          <button type="button" onClick={() => (isLast ? onClose() : setStepIndex((current) => Math.min(steps.length - 1, current + 1)))} className="welcome-tutorial__next" data-testid="tutorial-next">
            {isLast ? (language === 'es' ? 'Entendido' : 'Got it') : (language === 'es' ? 'Siguiente' : 'Next')}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}

function CoLiverProfileSheet({ profile, house, language, onClose }) {
  
  const fullName = profile.name || '';
  const displayName = fullName;

  
  const interestList = (profile.interests || '')
    .split(/[,;|]/)
    .map(s => s.trim())
    .filter(Boolean);
  const visibleInterests = interestList.slice(0, 4);
  const moreCount = interestList.length - visibleInterests.length;
  const educationItems = [profile.studies, profile.university].filter(Boolean);

  
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const content = (
    <div className="community-profile-modal-backdrop" data-testid="coliver-profile-sheet">
      <div className="community-profile-modal" role="dialog" aria-modal="true" aria-label={language === 'es' ? 'Perfil público' : 'Public profile'}>
        <div
          className="coliver-profile__header flex items-center justify-between py-3 bg-background"
          style={{ position: 'sticky', top: 0, zIndex: 2, paddingTop: 'calc(0.75rem + env(safe-area-inset-top))' }}
        >
          <h1 className="font-heading font-bold text-xl text-foreground">{language === 'es' ? 'Perfil' : 'Profile'}</h1>
          <button onClick={onClose} className="rounded-lg p-2 text-foreground hover:bg-muted" aria-label="Close" data-testid="coliver-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="community-profile-modal__content mx-auto px-5 pb-8">

        {}
        <div className="coliver-profile__hero flex items-start gap-5 mt-2">
          <div className="w-28 h-28 rounded-full overflow-hidden border border-border bg-muted flex-shrink-0" data-testid="coliver-avatar">
            <Avatar src={profile.photo_url} name={displayName} iconClassName="w-12 h-12 text-muted-foreground" initialClassName="font-heading font-bold text-3xl" />
          </div>
          <div className="coliver-profile__identity flex-1 min-w-0 pt-1">
            <h2 className="font-heading font-bold text-3xl text-foreground tracking-tight" data-testid="coliver-name">
              {displayName}
            </h2>
            {(profile.nationality || house) && (
              <div className="coliver-profile__location" data-testid="coliver-location">
                {house && <span className="coliver-profile__house-name"><House className="h-4 w-4" aria-hidden="true" />{house.name}</span>}
                {profile.nationality && <span className="coliver-profile__country" aria-label={profile.nationality}>{getCountryFlag(profile.nationality)} {profile.nationality}</span>}
              </div>
            )}
          </div>
        </div>

        {(educationItems.length > 0 || visibleInterests.length > 0) && (
          <section className="coliver-profile__about" data-testid="coliver-about">
            <h3 className="coliver-profile__about-title">
              {language === 'es' ? `Sobre ${displayName}` : `About ${displayName}`}
            </h3>

            {educationItems.length > 0 && (
              <div className="coliver-profile__about-group" data-testid="coliver-education">
                <p className="coliver-profile__about-label">{language === 'es' ? 'Estudios' : 'Studies'}</p>
                {educationItems.map((item, i) => <p key={i} className="coliver-profile__about-value">{item}</p>)}
              </div>
            )}

            {visibleInterests.length > 0 && (
              <div className="coliver-profile__about-group" data-testid="coliver-interests">
                <p className="coliver-profile__about-label">{language === 'es' ? 'Le gusta' : 'Likes'}</p>
                <div className="coliver-profile__interests flex flex-wrap gap-2">
                  {visibleInterests.map((interest, i) => (
                    <span key={i} className="coliver-profile__interest-chip px-4 py-2 rounded-full text-sm font-semibold text-foreground">
                      {interest}
                    </span>
                  ))}
                  {moreCount > 0 && (
                    <span className="px-4 py-2 rounded-full border border-border bg-muted text-sm font-semibold text-muted-foreground">
                      +{moreCount} {language === 'es' ? 'más' : 'more'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {}
        {profile.bio && (
          <div className="coliver-profile__bio p-5 mt-6" data-testid="coliver-bio">
            <h3 className="font-heading font-bold text-lg text-foreground mb-2">
              {language === 'es' ? 'Sobre mí' : 'About me'}
            </h3>
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{profile.bio}</p>
          </div>
        )}

        {}
        {(profile.instagram || profile.tiktok || profile.facebook) && (
          <div className="mt-6">
            <h3 className="font-heading font-bold text-lg text-foreground mb-3">
              {language === 'es' ? 'Redes' : 'Social'}
            </h3>
            <div className="coliver-profile__socials grid grid-cols-2 gap-3" data-testid="coliver-socials">
              {profile.instagram && (
                <a
                  href={`https://instagram.com/${profile.instagram.replace(/^@/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="coliver-profile__social-card p-4 flex items-center gap-3 hover:bg-muted transition-colors"
                  data-testid="coliver-ig"
                >
                  <Instagram className="coliver-profile__social-mark coliver-profile__social-mark--instagram h-5 w-5" aria-hidden="true" />
                  <span className="font-semibold text-foreground truncate">Instagram</span>
                </a>
              )}
              {profile.tiktok && (
                <a
                  href={`https://tiktok.com/@${profile.tiktok.replace(/^@/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="coliver-profile__social-card p-4 flex items-center gap-3 hover:bg-muted transition-colors"
                  data-testid="coliver-tt"
                >
                  <svg className="coliver-profile__social-mark coliver-profile__social-mark--tiktok h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M16.7 5.2a4.8 4.8 0 0 0 3.1 1.2v3.1a7.9 7.9 0 0 1-3.1-.7v6.1a5.9 5.9 0 1 1-5.9-5.9c.3 0 .7 0 1 .1v3.2a2.7 2.7 0 1 0 1.8 2.6V3.8h3.1v1.4Z" fill="currentColor" />
                  </svg>
                  <span className="font-semibold text-foreground truncate">TikTok</span>
                </a>
              )}
              {profile.facebook && (
                <a
                  href={profile.facebook.startsWith('http') ? profile.facebook : `https://facebook.com/${profile.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="coliver-profile__social-card p-4 flex items-center gap-3 hover:bg-muted transition-colors"
                  data-testid="coliver-fb"
                >
                  <Facebook className="coliver-profile__social-mark coliver-profile__social-mark--facebook h-5 w-5" aria-hidden="true" />
                  <span className="font-semibold text-foreground truncate">Facebook</span>
                </a>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function ProfileTab({ user, logout, onEditProfile, t, language, onChangeTheme, theme, onChangeLanguage }) {
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(true);
  const [pushError, setPushError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [openSection, setOpenSection] = useState(null); 
  const [openFaqCategory, setOpenFaqCategory] = useState(null);
  const [openFaqQuestion, setOpenFaqQuestion] = useState(null);

  const faqCategories = [
    {
      id: 'app',
      label: language === 'es' ? 'Uso de la app' : 'Using the app',
      icon: House,
      tone: 'blue',
      questions: [
        { q: language === 'es' ? '¿Cómo hago un reclamo?' : 'How do I make a claim?', a: language === 'es' ? 'Tocá el logo central de WannaColive en la barra inferior y elegí “Nuevo reclamo”.' : 'Tap the central WannaColive logo in the bottom bar and choose “New claim”.' },
        { q: language === 'es' ? '¿Cómo registro una visita?' : 'How do I register a visit?', a: language === 'es' ? 'Desde el menú central elegí “Registrar visita” y completá los datos del visitante y de la visita.' : 'From the central menu, choose “Register visit” and complete the visitor and visit details.' },
        { q: language === 'es' ? '¿Cómo reservo un espacio?' : 'How do I reserve a space?', a: language === 'es' ? 'Entrá en “Servicios” → “Reservar espacio”, elegí un espacio, una fecha y un horario disponible.' : 'Open “Services” → “Reserve space”, then choose a space, date and available time.' },
        { q: language === 'es' ? '¿Cómo solicito un cambio de habitación?' : 'How do I request a room change?', a: language === 'es' ? 'La opción “Cambio de casa” todavía figura como “Próximamente”. Si necesitás gestionarlo, contactá al equipo de WannaColive.' : 'The “Transfer” option is currently marked “Coming soon”. Contact the WannaColive team if you need to arrange it.' },
      ],
    },
    {
      id: 'claims',
      label: language === 'es' ? 'Reclamos' : 'Claims',
      icon: AlertTriangle,
      tone: 'green',
      questions: [
        { q: language === 'es' ? '¿Cómo consulto el estado de mi reclamo?' : 'How do I check my claim status?', a: language === 'es' ? 'Entrá en “Mis Reclamos” y revisá el estado que aparece en la tarjeta de cada reclamo.' : 'Open “My Claims” and check the status shown on each claim card.' },
        { q: language === 'es' ? '¿Cómo marco un reclamo como resuelto?' : 'How do I mark a claim as resolved?', a: language === 'es' ? 'Abrí “Mis Reclamos”, buscá el reclamo correspondiente y tocá “Marcar como resuelto”.' : 'Open “My Claims”, find the relevant claim and tap “Mark as resolved”.' },
        { q: language === 'es' ? '¿Qué hago si mi reclamo es urgente?' : 'What should I do if my claim is urgent?', a: language === 'es' ? 'Creá el reclamo desde “Nuevo reclamo” y contactá a Administración desde “Mensajes” para avisar la urgencia.' : 'Create the claim from “New claim” and contact Administration from “Messages” to flag the urgency.' },
        { q: language === 'es' ? '¿Cómo hablo con Administración sobre un reclamo?' : 'How do I talk to Administration about a claim?', a: language === 'es' ? 'Usá la acción “Mensajes” para abrir el chat con Administración.' : 'Use the “Messages” action to open the chat with Administration.' },
      ],
    },
    {
      id: 'visits',
      label: language === 'es' ? 'Visitas' : 'Visits',
      icon: UserRoundPlus,
      tone: 'violet',
      questions: [
        { q: language === 'es' ? '¿Cómo invito a una persona?' : 'How do I invite someone?', a: language === 'es' ? 'Si contás con una habitación privada, solicitá una visita desde “Registrar visita” en el menú central y esperá la aprobación de Administración.' : 'If your room is exclusive, request a visit from “Register visit” in the central menu and wait for Administration approval.' },
        { q: language === 'es' ? '¿Qué datos necesito de un visitante?' : 'What visitor details do I need?', a: language === 'es' ? 'El formulario solicita nombre, DNI, fecha de nacimiento, fecha de visita y notas opcionales.' : 'The form asks for the visitor’s name, ID, birth date, visit date and optional notes.' },
        { q: language === 'es' ? '¿Cómo cancelo o modifico una visita?' : 'How do I cancel or modify a visit?', a: language === 'es' ? 'Podés cancelar una visita desde “Mis Reclamos” → “Visitas”. La modificación directa no está disponible actualmente.' : 'You can cancel a visit from “My Claims” → “Visits”. Direct editing is not currently available.' },
      ],
    },
    {
      id: 'reservations',
      label: language === 'es' ? 'Reservas' : 'Reservations',
      icon: CalendarDays,
      tone: 'yellow',
      questions: [
        { q: language === 'es' ? '¿Cómo reservo un espacio?' : 'How do I reserve a space?', a: language === 'es' ? 'Desde “Servicios” elegí “Reservar espacio”, seleccioná un espacio y completá la reserva.' : 'From “Services”, choose “Reserve space”, select a space and complete the reservation.' },
        { q: language === 'es' ? '¿Cómo elijo fecha y horario?' : 'How do I choose a date and time?', a: language === 'es' ? 'Dentro del modal de reserva elegí la fecha y luego uno de los horarios disponibles.' : 'In the reservation modal, choose a date and then one of the available time slots.' },
        { q: language === 'es' ? '¿Puedo cancelar una reserva?' : 'Can I cancel a reservation?', a: language === 'es' ? 'Sí. En “Reservar espacios”, dentro de “Mis próximas reservas”, tocá “Cancelar”.' : 'Yes. In “Reserve spaces”, under “My upcoming reservations”, tap “Cancel”.' },
      ],
    },
    {
      id: 'announcements',
      label: language === 'es' ? 'Comunicados' : 'Announcements',
      icon: Megaphone,
      tone: 'blue',
      questions: [
        { q: language === 'es' ? '¿Dónde veo los comunicados?' : 'Where can I see announcements?', a: language === 'es' ? 'Entrá en “Comunicados” desde la navegación o desde el acceso correspondiente en Home.' : 'Open “Announcements” from navigation or from its shortcut on Home.' },
        { q: language === 'es' ? '¿Cómo sé cuáles ya leí?' : 'How do I know which ones I read?', a: language === 'es' ? 'Usá el filtro “Sin leer”; además, cada comunicado mantiene su indicador mientras no lo hayas abierto.' : 'Use the “Unread” filter; each announcement also keeps its indicator until you open it.' },
      ],
    },
    {
      id: 'account',
      label: language === 'es' ? 'Cuenta y perfil' : 'Account and profile',
      icon: UserRound,
      tone: 'neutral',
      questions: [
        { q: language === 'es' ? '¿Cómo cambio mis datos?' : 'How do I change my details?', a: language === 'es' ? 'En “Perfil” elegí “Mis Datos” y tocá editar para actualizar tu información.' : 'In “Profile”, choose “My Data” and tap edit to update your information.' },
        { q: language === 'es' ? '¿Cómo cambio mi contraseña?' : 'How do I change my password?', a: language === 'es' ? 'En “Perfil” elegí “Cambiar contraseña” y completá el formulario.' : 'In “Profile”, choose “Change password” and complete the form.' },
        { q: language === 'es' ? '¿Cómo cambio el idioma?' : 'How do I change the language?', a: language === 'es' ? 'En “Perfil” usá el selector de idioma dentro de “Preferencias”.' : 'In “Profile”, use the language selector inside “Preferences”.' },
        { q: language === 'es' ? '¿Cómo cambio entre modo claro y oscuro?' : 'How do I switch between light and dark mode?', a: language === 'es' ? 'En “Perfil” usá el selector de tema dentro de “Preferencias”.' : 'In “Profile”, use the theme selector inside “Preferences”.' },
      ],
    },
  ];

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPhone|iPad|iPod/.test(userAgent) || (
    /Macintosh/.test(userAgent) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1
  );
  const isAndroid = /Android/i.test(userAgent);
  const isSafari = /Safari/i.test(userAgent) && !/CriOS|Chrome|Chromium|FxiOS|EdgiOS|OPiOS|Android/i.test(userAgent);
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );
  const installPlatform = isAndroid ? 'android' : 'ios';
  const showInstallCard = !isStandalone && (isIOS || isAndroid);
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  useEffect(() => { checkPushStatus(); }, []);

  const checkPushStatus = async () => {
    if (!pushService.isSupported()) { setPushSupported(false); return; }
    const subscription = await pushService.getSubscription();
    setPushEnabled(!!subscription);
    
    
    
    
    if (subscription) {
      pushService.syncSubscription().catch(() => {});
    }
  };

  const togglePush = async () => {
    setPushLoading(true);
    setPushError('');
    try {
      if (pushEnabled) {
        await pushService.unsubscribe();
        setPushEnabled(false);
      } else {
        const permission = await pushService.requestPermission();
        if (permission !== 'granted') {
          setPushError(language === 'es'
            ? 'Permiso denegado. Activá las notificaciones desde Ajustes del sistema.'
            : 'Permission denied. Enable notifications in your system settings.');
        } else {
          await pushService.subscribe();
          setPushEnabled(true);
        }
      }
    } catch (error) {
      console.error('[Push] toggle error:', error);
      setPushError(error.message || 'Error');
    }
    setPushLoading(false);
  };

  const formatDate = (s) => {
    const fallback = language === 'es' ? 'Sin cargar' : 'Not set';
    if (!s) return fallback;
    try {
      const out = formatDateAR(s, language === 'es' ? 'es-AR' : 'en-US');
      return out || fallback;
    } catch { return fallback; }
  };

  const menuItems = [
    { id: 'data', label: language === 'es' ? 'Mis Datos' : 'My Data', description: language === 'es' ? 'Foto, contacto y perfil público' : 'Photo, contact and public profile', icon: UserRound, tone: 'account' },
    { id: 'contract', label: language === 'es' ? 'Contrato' : 'Contract', description: language === 'es' ? 'Fechas y datos de tu estadía' : 'Stay dates and details', icon: FileText, tone: 'account' },
    { id: 'password', label: language === 'es' ? 'Cambiar contraseña' : 'Change password', description: language === 'es' ? 'Seguridad de la cuenta' : 'Account security', icon: LockKeyhole, tone: 'account' },
    { id: 'notifications', label: language === 'es' ? 'Notificaciones' : 'Notifications', description: language === 'es' ? 'Configuración y permisos' : 'Settings and permissions', icon: Bell, tone: 'support' },
    { id: 'help', label: language === 'es' ? 'Ayuda y preguntas frecuentes' : 'Help & FAQ', description: language === 'es' ? 'Consultas frecuentes' : 'Frequently asked questions', icon: LifeBuoy, tone: 'support' },
    { id: 'emergency', label: language === 'es' ? 'Emergencias' : 'Emergencies', icon: Siren, tone: 'emergency' },
  ];

  const menuGroups = [
    { id: 'account', label: language === 'es' ? 'Cuenta' : 'Account', items: menuItems.filter((item) => item.tone === 'account') },
    { id: 'support', label: language === 'es' ? 'Soporte' : 'Support', items: menuItems.filter((item) => item.tone === 'support' || item.tone === 'emergency') },
  ];

  const onMenuClick = (id) => {
    if (id === 'password') return setShowChangePassword(true);
    if (id === 'data') return onEditProfile();
    if (id === 'help') {
      setOpenFaqCategory(null);
      setOpenFaqQuestion(null);
    }
    setOpenSection(id);
  };

  return (
    <div className="space-y-5 animate-enter" data-testid="guest-profile-tab">
      {}
      <div className="profile-page__header flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl text-foreground" data-testid="profile-title">
          {language === 'es' ? 'Perfil' : 'Profile'}
        </h1>
        <button onClick={logout} className="p-2 rounded-lg hover:bg-muted text-foreground" aria-label="Logout" data-testid="profile-logout-icon">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {}
      <div className="profile-page__user flex items-center gap-4" data-testid="profile-header-card">
        <div className="profile-page__avatar w-20 h-20 rounded-full bg-muted overflow-hidden flex-shrink-0">
          <Avatar src={user?.photo_url} name={user?.name} />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading font-bold text-xl text-foreground truncate">{user?.name}</h2>
          <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
        </div>
        <button onClick={onEditProfile} className="ml-auto p-2 rounded-lg hover:bg-muted text-foreground" aria-label="Edit" data-testid="profile-edit-btn">
          <Edit2 className="w-4 h-4" />
        </button>
      </div>

      {}
      {showInstallCard && (
        <div
          className="card-brutal-static rounded-2xl border border-border bg-surface p-4 shadow-sm"
          style={theme === 'dark' ? { boxShadow: 'none', border: '1px solid var(--border)' } : undefined}
          data-testid="install-guide-card"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground">
              <Smartphone className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-sm font-semibold text-foreground">{language === 'es' ? 'Instalá WannaColive' : 'Install WannaColive'}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{language === 'es' ? 'Tené acceso más rápido y una experiencia similar a una app.' : 'Get faster access and an app-like experience.'}</p>
            </div>
            <button type="button" onClick={() => setShowInstallGuide(true)} className="shrink-0 rounded-lg border border-border bg-muted px-3 py-2 text-xs font-semibold text-foreground transition-colors hover:bg-surface-hover" data-testid="install-guide-open">
              {language === 'es' ? 'Cómo instalar' : 'How to install'}
            </button>
          </div>
        </div>
      )}

      <InstallGuideModal
        open={showInstallGuide}
        initialPlatform={installPlatform}
        isIOSBrowser={isIOS && !isSafari}
        theme={theme}
        language={language}
        onClose={() => setShowInstallGuide(false)}
      />

      <div className="profile-page__groups" data-testid="profile-menu">
        {menuGroups.map((group) => (
          <section className="profile-page__group" key={group.id} aria-labelledby={`profile-group-${group.id}`}>
            <h2 className="profile-page__group-title" id={`profile-group-${group.id}`}>{group.label}</h2>
            <div className="profile-page__group-card">
              {group.items.map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.id}
                    onClick={() => onMenuClick(m.id)}
                    className={`profile-page__menu-item profile-page__menu-item--${m.tone}`}
                    data-testid={`profile-menu-${m.id}`}
                  >
                    <span className="profile-page__menu-icon" aria-hidden="true"><Icon className="w-4 h-4" /></span>
                    <span className="profile-page__menu-copy">
                      <span className="profile-page__menu-label">{m.label}</span>
                      {m.description && <span className="profile-page__menu-description">{m.description}</span>}
                    </span>
                    <ChevronRight className="profile-page__menu-arrow" />
                  </button>
                );
              })}
            </div>
          </section>
        ))}

        <section className="profile-page__group" aria-labelledby="profile-group-preferences">
          <h2 className="profile-page__group-title" id="profile-group-preferences">{language === 'es' ? 'Preferencias' : 'Preferences'}</h2>
          <div className="profile-page__preferences">
            <button onClick={onChangeTheme} className="profile-page__preference" data-testid="profile-theme-toggle">
              <span className="profile-page__menu-icon profile-page__menu-icon--theme" aria-hidden="true">
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </span>
              <span className="profile-page__menu-copy"><span className="profile-page__menu-label">{theme === 'light' ? (language === 'es' ? 'Oscuro' : 'Dark') : (language === 'es' ? 'Claro' : 'Light')}</span></span>
            </button>
            <button onClick={onChangeLanguage} className="profile-page__preference" data-testid="profile-lang-toggle">
              <span className="profile-page__menu-icon profile-page__menu-icon--language" aria-hidden="true"><Globe className="w-4 h-4" /></span>
              <span className="profile-page__menu-copy"><span className="profile-page__menu-label">{language === 'es' ? 'Idioma' : 'Language'}</span><span className="profile-page__menu-description">{language === 'es' ? 'EN' : 'ES'}</span></span>
            </button>
          </div>
        </section>
      </div>

      {}
      <button
        onClick={logout}
        className="profile-page__logout w-full py-4 font-heading font-semibold text-base transition-colors"
        data-testid="logout-profile-button"
      >
        {language === 'es' ? 'Cerrar sesión' : 'Logout'}
      </button>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} language={language} />
      )}

      {openSection === 'contract' && (
        <ProfileSectionModal
          title={language === 'es' ? 'Contrato' : 'Contract'}
          onClose={() => setOpenSection(null)}
          className="profile-section-modal--dark-backdrop bg-black/70"
          rows={[
            { label: language === 'es' ? 'Habitación' : 'Room', value: user?.room_number ? `${user.room_number} (${user?.room_type === 'exclusive' ? (language === 'es' ? 'Privada' : 'Private') : (language === 'es' ? 'Compartida' : 'Shared')})` : '—' },
            { label: language === 'es' ? 'Inicio del contrato' : 'Lease start', value: formatDate(user?.lease_start) },
            { label: language === 'es' ? 'Fin del contrato' : 'Lease end', value: formatDate(user?.lease_end) },
          ]}
        />
      )}

      {openSection === 'notifications' && (
        <ProfileSectionModal
          title={language === 'es' ? 'Notificaciones' : 'Notifications'}
          onClose={() => setOpenSection(null)}
          className="profile-section-modal--dark-backdrop bg-black/70"
          customBody={
            <div className="space-y-3">
              {!pushSupported && (
                <p className="text-sm text-muted-foreground">{language === 'es' ? 'Tu dispositivo no soporta notificaciones push.' : 'Your device does not support push notifications.'}</p>
              )}
              {pushSupported && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-foreground text-sm">Push Notifications</p>
                      <p className="text-xs text-muted-foreground">
                        {pushEnabled ? (language === 'es' ? 'Activadas' : 'Enabled') : (language === 'es' ? 'Desactivadas' : 'Disabled')}
                      </p>
                    </div>
                    <button
                      onClick={togglePush}
                      disabled={pushLoading}
                      className={`px-4 py-2 text-sm font-semibold rounded-lg border-2 border-foreground transition-all ${
                        pushEnabled ? 'bg-foreground text-background' : 'bg-surface text-foreground hover:bg-muted'
                      } disabled:opacity-50`}
                      data-testid="toggle-push-button"
                    >
                      {pushLoading ? '…' : pushEnabled ? (language === 'es' ? 'Desactivar' : 'Disable') : (language === 'es' ? 'Activar' : 'Enable')}
                    </button>
                  </div>
                  {pushError && (
                    <p className="text-xs text-destructive" data-testid="push-error-msg">{pushError}</p>
                  )}
                  {isIOS && !isStandalone && (
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {language === 'es'
                        ? 'En iOS las notificaciones requieren instalar la app: Compartir → Agregar a pantalla de inicio.'
                        : 'On iOS notifications require installing the app: Share → Add to Home Screen.'}
                    </p>
                  )}
                </>
              )}
            </div>
          }
        />
      )}

      {openSection === 'help' && (
        <ProfileSectionModal
          title={language === 'es' ? 'Ayuda y preguntas frecuentes' : 'Help & FAQ'}
          onClose={() => setOpenSection(null)}
          className="profile-section-modal--faq bg-black/70"
          lockScroll
          customBody={
            <div className="profile-faq" data-testid="profile-faq">
              <div className="profile-faq__categories">
                {faqCategories.map((category) => {
                  const CategoryIcon = category.icon;
                  const isCategoryOpen = openFaqCategory === category.id;
                  return (
                    <div className={`profile-faq__category${isCategoryOpen ? ' is-open' : ''}`} key={category.id}>
                      <button
                        type="button"
                        className="profile-faq__category-trigger"
                        onClick={() => {
                          setOpenFaqCategory(isCategoryOpen ? null : category.id);
                          setOpenFaqQuestion(null);
                        }}
                        aria-expanded={isCategoryOpen}
                        data-testid={`profile-faq-category-${category.id}`}
                      >
                        <span className={`profile-faq__icon profile-faq__icon--${category.tone}`}>
                          <CategoryIcon className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                        </span>
                        <span className="profile-faq__category-label">{category.label}</span>
                        <ChevronRight className={`profile-faq__chevron${isCategoryOpen ? ' is-open' : ''}`} aria-hidden="true" />
                      </button>

                      {isCategoryOpen && (
                        <div className="profile-faq__questions">
                          {category.questions.map((item, questionIndex) => {
                            const questionId = `${category.id}-${questionIndex}`;
                            const isQuestionOpen = openFaqQuestion === questionId;
                            return (
                              <div className={`profile-faq__question${isQuestionOpen ? ' is-open' : ''}`} key={questionId}>
                                <button
                                  type="button"
                                  className="profile-faq__question-trigger"
                                  onClick={() => setOpenFaqQuestion(isQuestionOpen ? null : questionId)}
                                  aria-expanded={isQuestionOpen}
                                  data-testid={`profile-faq-question-${questionId}`}
                                >
                                  <span>{item.q}</span>
                                  <ChevronRight className={`profile-faq__chevron profile-faq__chevron--question${isQuestionOpen ? ' is-open' : ''}`} aria-hidden="true" />
                                </button>
                                {isQuestionOpen && <p className="profile-faq__answer">{item.a}</p>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          }
        />
      )}

      {openSection === 'emergency' && (
        <ProfileSectionModal
          title={language === 'es' ? 'Emergencias' : 'Emergencies'}
          onClose={() => setOpenSection(null)}
          className="profile-section-modal--dark-backdrop bg-black/70"
          customBody={
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                {language === 'es' ? 'Si estás ante una situación de emergencia, contactá inmediatamente:' : 'In an emergency, contact immediately:'}
              </p>
              <a href="tel:911" className="card-brutal-static p-4 flex items-center gap-3" data-testid="emergency-911">
                <Phone className="w-5 h-5" />
                <div>
                  <p className="font-bold text-foreground">911</p>
                  <p className="text-xs text-muted-foreground">{language === 'es' ? 'Emergencia general' : 'General emergency'}</p>
                </div>
              </a>
              <a href="tel:107" className="card-brutal-static p-4 flex items-center gap-3" data-testid="emergency-107">
                <Phone className="w-5 h-5" />
                <div>
                  <p className="font-bold text-foreground">107</p>
                  <p className="text-xs text-muted-foreground">{language === 'es' ? 'Ambulancia' : 'Ambulance'}</p>
                </div>
              </a>
              <a href="tel:100" className="card-brutal-static p-4 flex items-center gap-3" data-testid="emergency-100">
                <Phone className="w-5 h-5" />
                <div>
                  <p className="font-bold text-foreground">100</p>
                  <p className="text-xs text-muted-foreground">{language === 'es' ? 'Bomberos' : 'Fire' }</p>
                </div>
              </a>
              <p className="text-xs text-muted-foreground mt-3">
                {language === 'es' ? 'También podés crear un reclamo URGENTE desde la app — el equipo de la casa será notificado al instante.' : 'You can also create an URGENT claim from the app — the house team will be notified instantly.'}
              </p>
            </div>
          }
        />
      )}
    </div>
  );
}

function ProfileSectionModal({ title, rows, customBody, onClose, className = '', lockScroll = false }) {
  useBodyScrollLock(lockScroll);
  return (
    <div className={`fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 ${className}`.trim()} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="profile-section-modal">
      <div className="bg-surface border-2 border-foreground rounded-2xl w-full max-w-md modal-card max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-xl text-foreground">{title}</h3>
            <button onClick={onClose} className="p-1 text-foreground" aria-label="Close"><X className="w-5 h-5" /></button>
          </div>
          {customBody}
          {rows && (
            <div className="space-y-1">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-b-0">
                  <span className="text-sm text-muted-foreground">{r.label}</span>
                  <span className="text-sm font-semibold text-foreground">{r.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ServicesTab({ language, t, isExclusive, renewalEligible, counts, directThreadsUnread = 0, onNavigate, onNavigateClaims, onNewClaim, onNewVisit, onNewTransfer, onRequestRenewal, onOpenMessages }) {
  const goClaims = onNavigateClaims || ((sub) => onNavigate('claims'));
  const services = [
    { key: 'messages', label: language === 'es' ? 'Mensajes' : 'Messages', icon: MessageSquare, count: directThreadsUnread, onClick: onOpenMessages },
    { key: 'claims', label: language === 'es' ? 'Reclamos' : 'Claims', icon: AlertTriangle, count: counts.claims, onClick: () => goClaims('claims') },
    { key: 'visits', label: language === 'es' ? 'Visitas' : 'Visits', icon: UserRoundPlus, count: counts.visits, onClick: () => goClaims('visits'), show: isExclusive },
    { key: 'transfers', label: language === 'es' ? 'Cambio de casa' : 'Transfer', icon: Plane, count: counts.transfers, onClick: () => goClaims('transfers') },
    { key: 'reservations', label: language === 'es' ? 'Reservar espacio' : 'Reserve space', icon: Calendar, onClick: () => onNavigate('reservations') },
    { key: 'benefits', label: language === 'es' ? 'Beneficios' : 'Benefits', icon: Gift, count: counts.benefits, onClick: () => onNavigate('benefits') },
    { key: 'announcements', label: language === 'es' ? 'Comunicados' : 'Announcements', icon: Bell, count: counts.unread, onClick: () => onNavigate('announcements') },
    { key: 'renewal', label: language === 'es' ? 'Renovación de contrato' : 'Contract renewal', icon: FileText, onClick: onRequestRenewal, show: !!renewalEligible },
  ].filter(s => s.show !== false);

  const serviceByKey = Object.fromEntries(services.map((service) => [service.key, service]));
  const serviceToneClasses = {
    'new-claim': 'guest-home__quick-icon bg-accent-mint',
    claims: 'guest-home__quick-icon bg-accent-mint',
    'new-visit': 'guest-home__quick-icon bg-accent-lavender',
    visits: 'guest-home__quick-icon bg-accent-lavender',
    reservations: 'guest-home__quick-icon bg-accent-yellow',
    messages: 'guest-home__quick-icon icon-pill-blue',
    announcements: 'guest-home__quick-icon icon-pill-blue',
    transfers: 'guest-home__quick-icon icon-pill-blue',
  };
  const renderServiceCard = (service, variant = 'tile') => (
    <button
      key={service.key}
      onClick={service.onClick}
      className={`relative service-card service-card--${variant}`}
      data-testid={`service-${service.key}`}
      data-service-key={service.key}
    >
      <span className={`service-card__icon service-card__icon--${service.key} ${serviceToneClasses[service.key] || ''}`}>
        <service.icon className="w-5 h-5" strokeWidth={2} />
      </span>
      {service.key === 'messages' && service.count > 0 && (
        <span className="guest-bottom-nav__badge" data-testid="service-messages-unread-badge">
          {service.count}
        </span>
      )}
      <span className="service-card__body">
        <span className="service-card__title">{service.label}</span>
        {typeof service.count === 'number' && (
          <span className="service-card__meta">
            {service.count} {language === 'es' ? 'activos' : 'active'}
          </span>
        )}
        {service.description && <span className="service-card__description">{service.description}</span>}
      </span>
      {variant === 'row' && <ChevronRight className="service-card__arrow w-5 h-5" strokeWidth={1.8} />}
    </button>
  );
  const mostUsed = [
    { key: 'new-claim', label: language === 'es' ? 'Nuevo reclamo' : 'New claim', description: language === 'es' ? 'Report\u00e1 un problema' : 'Report a problem', icon: AlertTriangle, onClick: onNewClaim },
    ...(isExclusive ? [{ key: 'new-visit', label: language === 'es' ? 'Registrar visita' : 'Register visit', description: language === 'es' ? 'Avis\u00e1 qui\u00e9n viene' : 'Let us know who is coming', icon: UserRoundPlus, onClick: onNewVisit }] : []),
    { ...serviceByKey.reservations, key: 'reservations', description: language === 'es' ? 'Espacios comunes' : 'Common spaces' },
    { ...serviceByKey.messages, key: 'messages', description: language === 'es' ? 'Chat con administraci\u00f3n' : 'Chat with administration' },
  ];
  const requests = ['claims', 'visits', 'transfers'].map((key) => serviceByKey[key]).filter(Boolean);
  const secondary = ['benefits', 'announcements', 'renewal'].map((key) => serviceByKey[key]).filter(Boolean);

  return (
    <div className="guest-services space-y-6 animate-enter" data-testid="guest-services-tab">
      <h1 className="font-heading font-bold text-2xl text-foreground">
        {language === 'es' ? 'Servicios' : 'Services'}
      </h1>
      <p className="guest-services__subtitle text-sm text-muted-foreground">
        {language === 'es' ? 'Todo lo que podés gestionar desde la app.' : 'Everything you can manage from the app.'}
      </p>

      <section className="guest-services__section" aria-labelledby="services-most-used">
        <h2 id="services-most-used" className="guest-services__section-title">{language === 'es' ? 'M\u00e1s usado' : 'Most used'}</h2>
        <div className="guest-services__most-used">
          {mostUsed.map((service) => renderServiceCard(service))}
        </div>
      </section>

      <section className="guest-services__section" aria-labelledby="services-requests">
        <h2 id="services-requests" className="guest-services__section-title">{language === 'es' ? 'Solicitudes' : 'Requests'}</h2>
        <div className="guest-services__rows">
          {requests.map((service) => renderServiceCard(service, 'row'))}
        </div>
      </section>

      {secondary.length > 0 && (
        <section className="guest-services__section" aria-labelledby="services-secondary">
          <h2 id="services-secondary" className="guest-services__section-title">{language === 'es' ? 'Otros servicios' : 'Other services'}</h2>
          <div className="guest-services__rows">
            {secondary.map((service) => renderServiceCard(service, 'row'))}
          </div>
        </section>
      )}

      <div className="guest-services__section guest-services__quick-actions">
        <h2 className="guest-services__section-title">
          {language === 'es' ? 'Acciones rápidas' : 'Quick actions'}
        </h2>
        <button onClick={onNewClaim} className="w-full card-brutal-static p-4 flex items-center gap-3 text-left" data-testid="services-new-claim">
          <Plus className="w-5 h-5" />
          <span className="font-semibold text-foreground">{language === 'es' ? 'Nuevo reclamo' : 'New claim'}</span>
        </button>
        {isExclusive && (
          <button onClick={onNewVisit} className="w-full card-brutal-static p-4 flex items-center gap-3 text-left" data-testid="services-new-visit">
            <Plus className="w-5 h-5" />
            <span className="font-semibold text-foreground">{language === 'es' ? 'Registrar visita' : 'Register visit'}</span>
          </button>
        )}
        {}
        <div className="w-full card-brutal-static p-4 flex items-center gap-3 text-left opacity-60 cursor-not-allowed select-none" data-testid="services-new-transfer-disabled">
          <Plus className="w-5 h-5" />
          <div className="flex-1">
            <div className="font-semibold text-foreground">{language === 'es' ? 'Pedir cambio de casa' : 'Request transfer'}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{language === 'es' ? 'Próximamente' : 'Coming soon'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}


function ChangePasswordModal({ onClose, language }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError(language === 'es' ? 'La contraseña debe tener al menos 8 caracteres' : 'Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const result = await api.changePassword(currentPassword, newPassword);
      // token_version changed server-side: keep the freshly issued pair.
      storeSession(result);
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'es' ? 'Error al cambiar la contraseña' : 'Error changing password'));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop" data-testid="change-password-modal">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-md overflow-y-auto modal-card">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-xl text-foreground">
              {language === 'es' ? 'Cambiar contraseña' : 'Change password'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg text-foreground"><X className="w-5 h-5" /></button>
          </div>
          {success ? (
            <div className="p-4 bg-accent-mint/20 border border-accent-mint rounded-lg text-center">
              <Check className="w-8 h-8 mx-auto mb-2 text-accent-mint" />
              <p className="font-semibold text-foreground">
                {language === 'es' ? 'Contraseña actualizada' : 'Password updated'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">
                  {language === 'es' ? 'Contraseña actual' : 'Current password'}
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input-brutal w-full px-4 py-3"
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">
                  {language === 'es' ? 'Nueva contraseña' : 'New password'}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-brutal w-full px-4 py-3"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'es' ? 'Mínimo 8 caracteres' : 'At least 8 characters'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">
                  {language === 'es' ? 'Confirmar nueva contraseña' : 'Confirm new password'}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-brutal w-full px-4 py-3"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive rounded-lg">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 py-3 border border-border rounded-lg font-medium text-foreground hover:bg-muted">
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button type="submit" disabled={loading} className="btn-brutal flex-1 py-3 disabled:opacity-50" data-testid="submit-change-password-button">
                  {loading ? '...' : (language === 'es' ? 'Cambiar' : 'Change')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}


function getCountryFlag(code) {
  const flags = {
    AR: '🇦🇷', BR: '🇧🇷', CL: '🇨🇱', CO: '🇨🇴', MX: '🇲🇽', PE: '🇵🇪', UY: '🇺🇾', VE: '🇻🇪',
    ES: '🇪🇸', US: '🇺🇸', FR: '🇫🇷', DE: '🇩🇪', IT: '🇮🇹', GB: '🇬🇧', PT: '🇵🇹', OTHER: '🌍'
  };
  return flags[code] || '🌍';
}


function ClaimCard({ claim, language, showPhoto, muted = false, onClose, currentUserId, autoOpen, onAutoOpened, onRead }) {
  const labels = language === 'es' ? claimTypeLabels : claimTypeLabelsEn;
  const statuses = language === 'es' ? statusLabels : statusLabelsEn;
  const [lightbox, setLightbox] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const unread = Number(claim.unread_count || 0);
  const canClose = claim.status !== 'closed' && onClose;

  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoOpen && !chatOpen) {
      setChatOpen(true);
      if (onAutoOpened) onAutoOpened();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);
  
  return (
    <div className={`card-brutal p-4 ${muted ? 'opacity-90' : ''}`} data-testid={`claim-card-${claim.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`p-1.5 rounded-lg ${
            claim.claim_type === 'repair' ? 'bg-accent-peach' :
            claim.claim_type === 'cleaning' ? 'bg-accent-mint' :
            claim.claim_type === 'noise' ? 'bg-accent-lavender' : 'bg-muted'
          }`}>
            {claimTypeIcons[claim.claim_type]}
          </span>
          <span className="text-xs text-muted-foreground">{labels[claim.claim_type]}</span>
          {claim.status !== 'closed' && unread > 0 && (
            <span
              className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-white"
              data-testid={`claim-unread-${claim.id}`}
              title={language === 'es' ? `${unread} mensaje(s) sin leer` : `${unread} unread message(s)`}
            >
              {unread}
            </span>
          )}
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
          claim.status === 'pending' ? 'bg-accent-peach text-background' :
          claim.status === 'in_progress' ? 'bg-primary text-background' :
          claim.status === 'closed' ? 'bg-muted text-foreground border border-border' :
          'bg-accent-mint text-background'
        }`}>
          {statuses[claim.status] || claim.status}
        </span>
      </div>
      <h3 className="font-heading font-semibold text-foreground mb-1">{claim.title}</h3>
      <p className="text-sm text-muted-foreground line-clamp-2">{claim.description}</p>
      {showPhoto && claim.photo_url && (
        <button
          type="button"
          onClick={() => setLightbox(true)}
          className="mt-2 w-full block rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-foreground"
          data-testid={`claim-photo-${claim.id}`}
        >
          <img src={claim.photo_url} alt="" className="w-full h-32 object-cover" />
        </button>
      )}
      {claim.admin_response && (
        <div className="mt-2 p-2 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground">Respuesta:</p>
          <p className="text-sm text-foreground">{claim.admin_response}</p>
        </div>
      )}
      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDateAR(claim.created_at, language === 'es' ? 'es-AR' : 'en-US')}
        </span>
        {claim.room_number && (
          <span className="flex items-center gap-1">
            <Home className="w-3 h-3" />
            {language === 'es' ? 'Hab.' : 'Room'} {claim.room_number}
          </span>
        )}
      </div>
      {canClose && claim.status === 'resolved' && !confirmClose && (
        <>
          <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1 px-1">
            <Clock className="w-3 h-3" />
            {language === 'es' ? 'El admin marcó este reclamo como resuelto. Confirmá si querés cerrarlo.' : 'Admin marked this claim as resolved. Confirm to close it.'}
          </p>
          <button
            type="button"
            onClick={() => setConfirmClose(true)}
            className="mt-2 w-full px-3 py-2 text-xs font-semibold border-2 border-foreground bg-accent-mint rounded-lg hover:shadow-brutal-sm text-foreground flex items-center justify-center gap-2"
            data-testid={`claim-close-btn-${claim.id}`}
          >
            <Check className="w-3.5 h-3.5" />
            {language === 'es' ? 'Confirmar resolución y cerrar' : 'Confirm resolution & close'}
          </button>
        </>
      )}
      {canClose && (claim.status === 'pending' || claim.status === 'in_progress') && !confirmClose && (
        <button
          type="button"
          onClick={() => setConfirmClose(true)}
          className="mt-3 w-full px-3 py-2 text-xs font-semibold border border-border rounded-lg hover:bg-muted text-foreground flex items-center justify-center gap-2"
          data-testid={`claim-mark-resolved-btn-${claim.id}`}
        >
          <Check className="w-3.5 h-3.5" />
          {language === 'es' ? 'Marcar como resuelto' : 'Mark as resolved'}
        </button>
      )}
      {!chatOpen && claim.status !== 'closed' && (
        <button
          type="button"
          onClick={() => setChatOpen(true)}
          className="mt-2 w-full px-3 py-2 text-xs font-semibold border border-border rounded-lg hover:bg-muted text-foreground flex items-center justify-center gap-2"
          data-testid={`claim-chat-btn-${claim.id}`}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {language === 'es' ? 'Mensajes' : 'Messages'}
        </button>
      )}
      {chatOpen && (
        <ClaimChatModal
          claim={claim}
          currentUserId={currentUserId}
          language={language}
          guestBackdrop
          onRead={onRead}
          onClose={() => setChatOpen(false)}
        />
      )}
      {confirmClose && (
        <div className="mt-3 p-3 rounded-lg border border-border bg-muted/40 text-xs">
          <p className="mb-2 text-foreground">
            {claim.status === 'resolved'
              ? (language === 'es' ? '¿Confirmás que el problema está resuelto y querés cerrar el reclamo?' : 'Confirm the issue is resolved and close the claim?')
              : (language === 'es' ? '¿Confirmás que el problema se resolvió? El reclamo se cerrará.' : 'Confirm the issue is resolved? The claim will be closed.')}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={async () => { await onClose(claim.id); setConfirmClose(false); }}
              className="flex-1 py-2 bg-foreground text-background rounded-lg font-semibold"
              data-testid={`claim-close-confirm-${claim.id}`}
            >
              {language === 'es' ? 'Sí, cerrar' : 'Yes, close'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmClose(false)}
              className="flex-1 py-2 border border-border rounded-lg"
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
      {lightbox && <ImageLightbox src={claim.photo_url} alt="Foto del reclamo" onClose={() => setLightbox(false)} />}
    </div>
  );
}

function AnnouncementsTab({ announcements, loading, language, userId, onMarkRead, onBack, autoOpenId, onAutoOpened, onDetailOpen }) {
  const [filter, setFilter] = useState('all'); 
  const sorted = [...(announcements || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const unread = sorted.filter(a => !a.read_by?.includes(userId));
  const list = filter === 'unread' ? unread : sorted;

  return (
    <div className="space-y-4 animate-enter" data-testid="guest-announcements-tab">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-muted text-foreground" aria-label="Back" data-testid="announcements-back-btn">
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <h2 className="font-heading font-bold text-2xl text-foreground">
          {language === 'es' ? 'Comunicados' : 'Announcements'}
        </h2>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`flex-1 py-2 rounded-lg border-2 border-foreground text-sm font-semibold transition-colors ${filter === 'all' ? 'bg-primary text-background' : 'bg-surface text-foreground hover:bg-muted'}`}
          data-testid="announcements-filter-all"
        >
          {language === 'es' ? 'Todos' : 'All'} ({sorted.length})
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`flex-1 py-2 rounded-lg border-2 border-foreground text-sm font-semibold transition-colors ${filter === 'unread' ? 'bg-primary text-background' : 'bg-surface text-foreground hover:bg-muted'}`}
          data-testid="announcements-filter-unread"
        >
          {language === 'es' ? 'Sin leer' : 'Unread'} ({unread.length})
        </button>
      </div>

      {loading ? (
        <LoadingIndicator label={language === 'es' ? 'Cargando...' : 'Loading...'} className="py-6" />
      ) : list.length === 0 ? (
        <div className="announcements-empty-state card-brutal-static p-8 text-center text-muted-foreground">
          <Bell className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-semibold mb-1 text-foreground">
            {filter === 'unread'
              ? (language === 'es' ? 'Estás al día' : 'All caught up')
              : (language === 'es' ? 'No hay comunicados' : 'No announcements')}
          </p>
          <p className="text-xs">
            {filter === 'unread'
              ? (language === 'es' ? 'No tenés comunicados sin leer.' : 'You have no unread announcements.')
              : (language === 'es' ? 'Cuando los administradores publiquen un comunicado, aparecerá acá.' : 'When admins post announcements, they will appear here.')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map(a => (
            <AnnouncementCard
              key={a.id}
              announcement={a}
              userId={userId}
              onMarkRead={onMarkRead}
              language={language}
              autoOpen={autoOpenId === a.id}
              onAutoOpened={onAutoOpened}
              onDetailOpen={onDetailOpen}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AnnouncementCard({ announcement, userId, onMarkRead, language, autoOpen, onAutoOpened, onDetailOpen }) {
  const [showDetail, setShowDetail] = useState(false);
  const isRead = announcement.read_by?.includes(userId);
  const priorityVisual = getAnnouncementPriorityVisual(announcement.priority);
  const PriorityIcon = priorityVisual.icon;
  const priorityKey = announcementPriorityVisuals[announcement.priority] ? announcement.priority : 'info';

  useEffect(() => {
    if (!showDetail) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showDetail]);

  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoOpen && !showDetail) {
      setShowDetail(true);
      if (onDetailOpen) onDetailOpen(true);
      if (!isRead && onMarkRead) onMarkRead(announcement.id);
      if (onAutoOpened) onAutoOpened();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);
  const priorityLabel = (p) => ({
    multa: '🛑 Multa / Sanción',
    urgent: '🚨 Urgente',
    important: '⚠️ Importante',
    maintenance: '🔧 Mantenimiento',
    event: '🎉 Evento',
    info: 'ℹ️ Información',
  }[p] || 'ℹ️ Información');

  const handleClick = () => {
    setShowDetail(true);
    if (onDetailOpen) onDetailOpen(true);
    if (!isRead && onMarkRead) {
      onMarkRead(announcement.id);
    }
  };

  const closeDetail = () => {
    setShowDetail(false);
    if (onDetailOpen) onDetailOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`card-brutal p-4 w-full text-left transition-all ${!isRead ? 'border-l-4 border-l-primary' : ''}`}
        data-testid={`announcement-card-${announcement.id}`}
      >
        <div className="flex items-start justify-between mb-2">
          <span className={`announcement-priority-meta announcement-priority-meta--${priorityKey}`}>
            <span className="announcement-priority-icon" aria-hidden="true">
              <PriorityIcon className="w-3.5 h-3.5" strokeWidth={2} />
            </span>
            <span>{priorityVisual.label}</span>
          </span>
          {!isRead && <span className="w-2 h-2 bg-primary rounded-full" data-testid={`announcement-unread-dot-${announcement.id}`} />}
        </div>
        <h3 className="font-heading font-semibold text-foreground mb-1">{announcement.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{linkify(announcement.content)}</p>
        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
          <Calendar className="w-3 h-3" />
          {formatDateAR(announcement.created_at, language === 'es' ? 'es-AR' : 'en-US')} • {announcement.author_name}
        </div>
      </button>

      {showDetail && createPortal((
        <div
          className="announcement-detail-modal-backdrop fixed inset-0 bg-black/70 z-[100] modal-backdrop"
          onClick={closeDetail}
          data-testid={`announcement-detail-modal-${announcement.id}`}
        >
          <div
            className="announcement-detail-modal__panel bg-surface border-2 border-foreground rounded-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="announcement-detail-modal__header">
              <div className="flex items-start justify-between mb-4">
                <span className={`announcement-priority-meta announcement-priority-meta--${priorityKey}`}>
                  <span className="announcement-priority-icon" aria-hidden="true">
                    <PriorityIcon className="w-3.5 h-3.5" strokeWidth={2} />
                  </span>
                  <span>{priorityVisual.label}</span>
                </span>
                <button
                  onClick={closeDetail}
                  className="p-1 hover:bg-muted rounded-lg"
                  data-testid="announcement-close-button"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground mb-2">{announcement.title}</h2>
              <p className="text-xs text-muted-foreground mb-4 flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                {formatDateTimeAR(announcement.created_at, language === 'es' ? 'es-AR' : 'en-US')} • {announcement.author_name}
              </p>
            </div>
            <div className="announcement-detail-modal__body">
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed break-words">{linkify(announcement.content)}</p>
            </div>
            <div className="announcement-detail-modal__footer">
              <button
                onClick={() => {
                  if (!isRead && onMarkRead) onMarkRead(announcement.id);
                  closeDetail();
                }}
                className={`w-full py-3 mt-6 flex items-center justify-center gap-2 font-bold ${
                  announcement.priority === 'multa'
                    ? 'bg-destructive text-white rounded-xl shadow-lg border-2 border-foreground hover:opacity-90'
                    : 'btn-brutal'
                }`}
                data-testid="announcement-mark-read-button"
              >
                <Check className="w-4 h-4" />
                {announcement.priority === 'multa'
                  ? (language === 'es' ? '🛑 Darme por notificado' : 'Acknowledge fine')
                  : (language === 'es' ? 'Entendido / Leído' : 'Got it / Mark read')}
              </button>
              </div>
            </div>
          </div>
      ), document.body)}
    </>
  );
}

function VisitCard({ visit, language, onCancelled, muted = false }) {
  const [cancelling, setCancelling] = useState(false);
  const statusColors = {
    pending: 'bg-primary text-background',
    approved: 'bg-accent-mint text-background',
    rejected: 'bg-destructive text-white',
    cancelled: 'bg-muted text-foreground',
  };
  const statusLabels = {
    pending: language === 'es' ? 'Registrada' : 'Registered',
    approved: language === 'es' ? 'Registrada' : 'Registered',
    rejected: language === 'es' ? 'Anulada' : 'Cancelled',
    cancelled: language === 'es' ? 'Cancelada' : 'Cancelled',
  };
  const localizeDuration = (raw) => {
    if (!raw) return '';
    const m = /^(\d+)\s*nights?$/i.exec(raw);
    if (m) {
      const n = parseInt(m[1], 10);
      return language === 'es' ? (n === 1 ? '1 noche' : `${n} noches`) : (n === 1 ? '1 night' : `${n} nights`);
    }
    if (raw === 'weekend') return language === 'es' ? 'Fin de semana' : 'Weekend';
    if (raw === '1 week') return language === 'es' ? '1 semana' : '1 week';
    return raw;
  };
  const displayDuration = localizeDuration(visit.visit_duration);
  const visitCost = Number(visit.total_cost || 0);
  const visitCurrency = visit.currency || 'ARS';
  const visitCurrencySymbol = { ARS: '$', USD: 'US$', EUR: '€', UYU: '$U', BRL: 'R$' }[visitCurrency] || visitCurrency;

  
  
  
  
  const HOURS_24_MS = 24 * 60 * 60 * 1000;
  const arrivalMs = visit.visit_date
    ? new Date(`${visit.visit_date}T00:00:00`).getTime()
    : null;
  const moreThan24h = arrivalMs !== null && (arrivalMs - Date.now()) > HOURS_24_MS;
  const canCancel = visit.status !== 'cancelled' && visit.status !== 'rejected' && moreThan24h;

  const handleCancel = async () => {
    if (!window.confirm(language === 'es'
      ? '¿Cancelar esta visita? No se podrá revertir.'
      : 'Cancel this visit? This cannot be undone.')) return;
    setCancelling(true);
    try {
      await api.cancelVisit(visit.id);
      if (onCancelled) onCancelled(visit.id);
    } catch (e) {
      alert(e?.response?.data?.detail || (language === 'es' ? 'No pudimos cancelar la visita' : 'Could not cancel the visit'));
      setCancelling(false);
    }
  };

  return (
    <div className={`card-brutal p-4 ${muted ? 'opacity-90' : ''}`} data-testid={`visit-card-${visit.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="visit-card__visitor">
          <span className="visit-card__visitor-icon" aria-hidden="true">
            <UserRoundPlus className="w-4 h-4" />
          </span>
          <div>
          <h3 className="font-heading font-semibold text-foreground">{visit.visitor_name}</h3>
          <p className="text-sm text-muted-foreground">DNI: {visit.visitor_dni}</p>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[visit.status]}`}>
          {statusLabels[visit.status]}
        </span>
      </div>
      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {formatDateAR(visit.visit_date, language === 'es' ? 'es-AR' : 'en-US')}</span>
        <span>{displayDuration}</span>
      </div>
      {visitCost > 0 && (
        <div className="mt-2 flex items-center justify-between text-sm" data-testid={`visit-cost-${visit.id}`}>
          <span className="text-muted-foreground">{language === 'es' ? 'Cargo por visita' : 'Visit fee'}</span>
          <span className="font-mono font-semibold text-foreground">
            {visitCurrencySymbol} {visitCost.toLocaleString('es-AR')} {visitCurrency}
          </span>
        </div>
      )}
      {visit.rejection_reason && (
        <div className="mt-2 p-2 bg-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{visit.rejection_reason}</p>
        </div>
      )}
      {canCancel ? (
        <button
          type="button"
          onClick={handleCancel}
          disabled={cancelling}
          className="mt-3 w-full px-3 py-2 border-2 border-foreground rounded-lg bg-surface text-foreground hover:bg-destructive hover:text-white transition-colors text-sm font-medium disabled:opacity-50"
          data-testid={`cancel-visit-${visit.id}`}
        >
          {cancelling
            ? (language === 'es' ? 'Cancelando…' : 'Cancelling…')
            : (language === 'es' ? 'Cancelar visita' : 'Cancel visit')}
        </button>
      ) : visit.status !== 'cancelled' && visit.status !== 'rejected' && arrivalMs !== null && (arrivalMs - Date.now()) <= HOURS_24_MS && (arrivalMs - Date.now()) > -HOURS_24_MS ? (
        <p className="mt-3 text-xs text-muted-foreground italic" data-testid={`cancel-visit-locked-${visit.id}`}>
          {language === 'es'
            ? 'Ya no se puede cancelar (faltan menos de 24 hs).'
            : 'Cannot be cancelled anymore (less than 24h left).'}
        </p>
      ) : null}
    </div>
  );
}

function TransferCard({ transfer, language }) {
  const statusColors = {
    pending: 'bg-primary text-background',
    approved: 'bg-accent-mint text-background',
    rejected: 'bg-destructive text-white'
  };
  const statusLabels = {
    pending: language === 'es' ? 'Pendiente' : 'Pending',
    approved: language === 'es' ? 'Aprobado' : 'Approved',
    rejected: language === 'es' ? 'Rechazado' : 'Rejected'
  };

  return (
    <div className="card-brutal p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-heading font-semibold text-foreground flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {transfer.target_house_name}
          </h3>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[transfer.status]}`}>
          {statusLabels[transfer.status]}
        </span>
      </div>
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>{transfer.start_date} → {transfer.end_date}</span>
      </div>
      {transfer.reason && <p className="text-sm text-muted-foreground mt-2">{transfer.reason}</p>}
      {transfer.rejection_reason && (
        <div className="mt-2 p-2 bg-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{transfer.rejection_reason}</p>
        </div>
      )}
    </div>
  );
}


function NewClaimModal({ user, onClose, onSubmit, t, language }) {
  useBodyScrollLock();
  const [formData, setFormData] = useState({ claim_type: 'repair', title: '', description: '', location: '' });
  const [photoUrls, setPhotoUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newPhotos = [];
    for (const file of files.slice(0, 4 - photoUrls.length)) {
      const compressed = await compressImage(file);
      newPhotos.push(compressed);
    }
    setPhotoUrls(prev => [...prev, ...newPhotos].slice(0, 4));
  };

  const removePhoto = (index) => {
    setPhotoUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit({
      ...formData,
      photo_urls: photoUrls,
      photo_url: photoUrls[0] || '',
    });
    setLoading(false);
  };

  const labels = language === 'es' ? claimTypeLabels : claimTypeLabelsEn;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop" data-testid="new-claim-modal">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-md overflow-y-auto modal-card">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-xl text-foreground">{t('newClaim')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {user?.room_number && (
              <div className="flex items-center justify-between gap-3 p-3 bg-muted/40 border border-border rounded-lg" data-testid="claim-room-display">
                <span className="text-sm text-muted-foreground">{language === 'es' ? 'Tu habitación' : 'Your room'}</span>
                <span className="font-heading font-semibold text-foreground">{user.room_number}</span>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('claimType')}</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(labels).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, claim_type: value })}
                    className={`p-3 border border-border rounded-lg flex items-center gap-2 transition-all ${
                      formData.claim_type === value ? 'bg-primary text-background' : 'bg-surface text-foreground hover:bg-muted'
                    }`}
                  >
                    {claimTypeIcons[value]}
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('claimTitle')}</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                required
                data-testid="claim-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('claimDescription')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-brutal w-full px-4 py-3 min-h-[100px] resize-none"
                required
                data-testid="claim-description-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('claimLocation')}</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder={language === 'es' ? 'Ej: Cocina, Baño' : 'E.g., Kitchen, Bathroom'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground font-heading">
                {language === 'es' ? `Fotos (${photoUrls.length}/4)` : `Photos (${photoUrls.length}/4)`}
              </label>
              <input type="file" accept="image/*" multiple onChange={handlePhotoChange} ref={fileInputRef} className="hidden" />
              {photoUrls.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {photoUrls.map((url, idx) => (
                    <div key={idx} className="relative h-24 rounded-lg overflow-hidden border-2 border-foreground group">
                      <img src={url} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-1 right-1 p-1 bg-destructive text-white rounded-full shadow-md hover:scale-110 transition-transform"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {photoUrls.length < 4 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full p-3 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <Camera className="w-5 h-5" />
                  {photoUrls.length === 0
                    ? (language === 'es' ? 'Adjuntar fotos (hasta 4)' : 'Attach photos (up to 4)')
                    : (language === 'es' ? 'Agregar otra foto' : 'Add another photo')}
                </button>
              )}
            </div>
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 py-3 border border-border rounded-lg font-medium text-foreground hover:bg-muted">{t('cancel')}</button>
              <button type="submit" disabled={loading} className="new-claim-modal__submit btn-brutal flex-1 py-3 disabled:opacity-50">{loading ? '...' : t('create')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function NewVisitModal({ onClose, onSubmit, t, language = 'es', appSettings = {}, myHouse = null }) {
  useBodyScrollLock();
  const [formData, setFormData] = useState({ visitor_name: '', visitor_dni: '', visitor_photo_dni: '', visitor_birthdate: '', visit_date: '', visit_duration: '1 night', notes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);  
  const fileInputRef = useRef(null);

  const effectiveVisitCost = myHouse?.visit_cost_per_night !== null && myHouse?.visit_cost_per_night !== undefined
    ? Number(myHouse.visit_cost_per_night)
    : Number(appSettings?.visit_cost_per_night || 0);
  const effectiveVisitCurrency = (myHouse?.visit_cost_per_night !== null && myHouse?.visit_cost_per_night !== undefined)
    ? (myHouse?.visit_cost_currency || appSettings?.visit_cost_currency || 'ARS')
    : (appSettings?.visit_cost_currency || 'ARS');
  
  
  
  const nightsFromDuration = (raw) => {
    if (!raw) return 1;
    const m = /^(\d+)\s*nights?$/i.exec(raw);
    if (m) return parseInt(m[1], 10);
    if (raw === 'weekend') return 2;
    if (raw === '1 week') return 7;
    return 1;
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const compressed = await compressDocument(file);
      setFormData({ ...formData, visitor_photo_dni: compressed });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.visitor_photo_dni) {
      setError('Debés adjuntar una foto del DNI');
      return;
    }
    
    
    if (!formData.visitor_birthdate) {
      setError('Cargá la fecha de nacimiento del visitante.');
      return;
    }
    const b = new Date(formData.visitor_birthdate);
    const now = new Date();
    let age = now.getFullYear() - b.getFullYear();
    const monthDiff = now.getMonth() - b.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < b.getDate())) age--;
    if (age < 18 || age > 35) {
      setError(`La visita solo es válida para personas entre 18 y 35 años. Edad calculada: ${age}.`);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const created = await onSubmit(formData);
      const nights = Number(created?.nights) || nightsFromDuration(formData.visit_duration);
      const perNight = created?.cost_per_night !== undefined && created?.cost_per_night !== null
        ? Number(created.cost_per_night)
        : effectiveVisitCost;
      setConfirmation({
        nights,
        cost: created?.total_cost !== undefined && created?.total_cost !== null
          ? Number(created.total_cost)
          : nights * perNight,
        currency: created?.currency || effectiveVisitCurrency || 'ARS',
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear solicitud');
    }
    setLoading(false);
  };

  
  if (confirmation) {
    const { nights, cost, currency } = confirmation;
    return (
      <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop" data-testid="visit-confirmed-modal">
        <div className="bg-surface border border-border rounded-2xl w-full max-w-md modal-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-accent-mint flex items-center justify-center border-2 border-foreground text-xl">✓</div>
            <div>
              <h2 className="font-heading font-bold text-xl">{language === 'es' ? '¡Visita confirmada!' : 'Visit confirmed!'}</h2>
              <p className="text-xs text-muted-foreground">{language === 'es' ? 'Te dejamos los detalles del cargo.' : 'Here are the charge details.'}</p>
            </div>
          </div>
          <div className="p-4 rounded-lg border-2 border-foreground bg-background space-y-2 mb-5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{language === 'es' ? 'Visitante' : 'Visitor'}</span>
              <span className="font-medium">{formData.visitor_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{language === 'es' ? 'Duración' : 'Duration'}</span>
              <span className="font-medium">{language === 'es' ? (nights === 1 ? '1 noche' : `${nights} noches`) : (nights === 1 ? '1 night' : `${nights} nights`)}</span>
            </div>
            {cost > 0 && (
              <div className="flex justify-between text-sm pt-2 border-t border-border">
                <span className="text-muted-foreground">{language === 'es' ? 'Costo de visita' : 'Visit fee'}</span>
                <span className="font-mono font-semibold">
                  {({ ARS: '$', USD: 'US$', EUR: '€', UYU: '$U', BRL: 'R$' }[currency] || currency)} {cost.toLocaleString('es-AR')} {currency}
                </span>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
            {cost > 0
              ? (language === 'es' ? 'Este importe se abona en administración. La visita queda registrada y aprobada automáticamente.' : 'This amount must be paid at administration. The visit is registered and automatically approved.')
              : (language === 'es' ? 'Tu visita queda registrada y aprobada automáticamente.' : 'Your visit is registered and automatically approved.')}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-lg bg-foreground text-background font-semibold"
            data-testid="visit-confirmed-close"
          >
            {language === 'es' ? 'Entendido' : 'Got it'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-md overflow-y-auto modal-card">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-xl text-foreground">{t('requestVisit')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg text-foreground"><X className="w-5 h-5" /></button>
          </div>
          {error && <div className="mb-4 p-3 bg-destructive/20 border border-destructive rounded-lg text-sm text-destructive">{error}</div>}

          {}
          {(() => {
            const perNight = effectiveVisitCost;
            const currency = effectiveVisitCurrency;
            const currencySymbol = { ARS: '$', USD: 'US$', EUR: '€', UYU: '$U', BRL: 'R$' }[currency] || currency;
            return (
              <div className="mb-3 p-3 rounded-lg border border-border bg-muted/40 flex items-start gap-2" data-testid="visit-fee-notice">
                <span className="w-5 h-5 rounded-full bg-foreground text-background text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">!</span>
                <p className="text-xs text-foreground leading-relaxed">
                  {perNight > 0
                    ? <>Las visitas tienen un cargo de <strong>{currencySymbol} {perNight.toLocaleString('es-AR')} {currency} por noche</strong>, que se deberá abonar en administración.</>
                    : <>Las visitas quedan registradas y aprobadas automáticamente.</>}
                </p>
              </div>
            );
          })()}

          {}
          <div className="mb-4 p-3 rounded-lg border-2 border-foreground bg-accent-yellow flex items-start gap-2" data-testid="visit-age-notice">
            <span className="w-5 h-5 rounded-full bg-foreground text-background text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">i</span>
            <p className="text-xs text-foreground leading-relaxed">
              La visita <strong>solo es válida para personas entre 18 y 35 años</strong>. Cargá la fecha de nacimiento del visitante para confirmar la reserva.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('visitorName')} *</label>
              <input type="text" value={formData.visitor_name} onChange={(e) => setFormData({ ...formData, visitor_name: e.target.value })} className="input-brutal w-full px-4 py-3" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('visitorDni')} *</label>
              <input type="text" value={formData.visitor_dni} onChange={(e) => setFormData({ ...formData, visitor_dni: e.target.value })} className="input-brutal w-full px-4 py-3" required data-testid="visit-dni-input" />
              <p className="text-[11px] text-destructive mt-1.5 leading-snug" data-testid="visit-dni-warning">
                ⚠ El DNI es <strong>obligatorio</strong>. Si no lo cargás, se te aplicará una <strong>multa por no adjuntar el DNI del visitante</strong>.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Fecha de nacimiento del visitante *</label>
              <NativeDateField
                value={formData.visitor_birthdate}
                onChange={(e) => setFormData({ ...formData, visitor_birthdate: e.target.value })}
                max={new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                min={new Date(Date.now() - 36 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                required
                data-testid="visit-birthdate-input"
                language={language}
              />
              {(() => {
                if (!formData.visitor_birthdate) return null;
                const b = new Date(formData.visitor_birthdate);
                const now = new Date();
                let age = now.getFullYear() - b.getFullYear();
                const m = now.getMonth() - b.getMonth();
                if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
                const valid = age >= 18 && age <= 35;
                return (
                  <p className={`text-[11px] mt-1.5 leading-snug ${valid ? 'text-foreground/70' : 'text-destructive'}`} data-testid="visit-age-computed">
                    {valid
                      ? `Edad: ${age} años · dentro del rango permitido (18-35).`
                      : `Edad: ${age} años · ⚠ fuera del rango permitido (18-35). No podrás registrar la visita.`}
                  </p>
                );
              })()}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('visitorPhotoDni')} *</label>
              <input type="file" accept="image/*" onChange={handlePhotoChange} ref={fileInputRef} className="hidden" />
              {formData.visitor_photo_dni ? (
                <div className="relative">
                  <img src={formData.visitor_photo_dni} alt="" className="w-full h-40 object-cover rounded-lg" />
                  <button type="button" onClick={() => setFormData({ ...formData, visitor_photo_dni: '' })} className="absolute top-2 right-2 p-1 bg-destructive text-white rounded-full"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full p-4 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary transition-colors flex items-center justify-center gap-2">
                  <Camera className="w-5 h-5" />
                  {t('attachPhoto')}
                </button>
              )}
            </div>
            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">{t('visitDate')} *</label>
                <NativeDateField
                  value={formData.visit_date}
                  onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                  required
                  language={language}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">{t('visitDuration')} *</label>
                <select value={formData.visit_duration} onChange={(e) => setFormData({ ...formData, visit_duration: e.target.value })} className="input-brutal w-full px-4 py-3">
                  {}
                  {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => {
                    const key = n === 1 ? '1 night' : `${n} nights`;
                    const label = language === 'es'
                      ? (n === 1 ? '1 noche' : `${n} noches`)
                      : (n === 1 ? '1 night' : `${n} nights`);
                    return <option key={key} value={key}>{label}</option>;
                  })}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('notes')}</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input-brutal w-full px-4 py-3 min-h-[80px] resize-none" />
            </div>
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 py-3 border border-border rounded-lg font-medium text-foreground hover:bg-muted">{t('cancel')}</button>
              <button type="submit" disabled={loading} className="new-visit-modal__submit btn-brutal flex-1 py-3 disabled:opacity-50">{loading ? '...' : t('create')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function NewTransferModal({ onClose, onSubmit, houses, currentHouseId, t }) {
  useBodyScrollLock();
  const [formData, setFormData] = useState({ target_house_id: '', start_date: '', end_date: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const availableHouses = houses.filter(h => h.id !== currentHouseId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear solicitud');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-md overflow-y-auto modal-card">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-xl text-foreground">{t('requestTransfer')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg text-foreground"><X className="w-5 h-5" /></button>
          </div>
          {error && <div className="mb-4 p-3 bg-destructive/20 border border-destructive rounded-lg text-sm text-destructive">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('targetHouse')} *</label>
              <select value={formData.target_house_id} onChange={(e) => setFormData({ ...formData, target_house_id: e.target.value })} className="input-brutal w-full px-4 py-3" required>
                <option value="">Seleccionar...</option>
                {availableHouses.map(house => <option key={house.id} value={house.id}>{house.name} - {house.city}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">{t('startDate')} *</label>
                <input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="input-brutal w-full px-4 py-3" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">{t('endDate')} *</label>
                <input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="input-brutal w-full px-4 py-3" required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('reason')}</label>
              <textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} className="input-brutal w-full px-4 py-3 min-h-[80px] resize-none" placeholder="Ej: Viaje por trabajo, vacaciones..." />
            </div>
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 py-3 border border-border rounded-lg font-medium text-foreground hover:bg-muted">{t('cancel')}</button>
              <button type="submit" disabled={loading} className="btn-brutal flex-1 py-3 disabled:opacity-50">{loading ? '...' : t('create')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function EditProfileModal({ user, onClose, onSubmit, t, language = 'es' }) {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    photo_url: user?.photo_url || '',
    university: user?.university || '',
    instagram: user?.instagram || '',
    facebook: user?.facebook || '',
    tiktok: user?.tiktok || '',
    interests: user?.interests || '',
    nationality: user?.nationality || '',
    studies: user?.studies || '',
    share_profile: user?.share_profile || false
  });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoChanged, setPhotoChanged] = useState(false);
  const fileInputRef = useRef(null);
  const { cropImage, cropperNode } = useImageCropper();

  useEffect(() => {
    api.getCountries().then(setCountries).catch(console.error);
  }, []);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    const cropped = await cropImage(file, { aspect: 1, title: 'Encuadrar foto de perfil' });
    if (!cropped) return;
    const compressed = await compressAvatar(cropped);
    setFormData((prev) => ({ ...prev, photo_url: compressed }));
    setPhotoChanged(true);
  };

  const handleRemovePhoto = () => {
    setFormData({ ...formData, photo_url: '' });
    setPhotoChanged(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      
      
      const payload = { ...formData };
      delete payload.name;
      // No reenviar la foto si no cambió: `formData.photo_url` ya viene como
      // URL firmada (o data URI legacy). Enviarla de vuelta haría que el
      // backend la guarde como si fuera una key nueva y rompería el avatar.
      if (!photoChanged) {
        delete payload.photo_url;
      }
      const newEmail = (payload.email || '').trim().toLowerCase();
      if (newEmail === (user?.email || '').toLowerCase()) {
        delete payload.email;
      } else {
        payload.email = newEmail;
      }
      await onSubmit(payload);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop">
      {cropperNode}
      <div className="edit-profile-modal__panel bg-surface border border-border rounded-xl w-full max-w-md overflow-y-auto modal-card">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-xl text-foreground">{t('editProfile')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg text-foreground"><X className="w-5 h-5" /></button>
          </div>
          {error && <div className="mb-4 p-3 bg-destructive/20 border border-destructive rounded-lg text-sm text-destructive">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            {}
            <div className="flex flex-col items-center">
              <div className="w-24 h-24 rounded-full bg-muted border-2 border-primary overflow-hidden mb-2">
                {formData.photo_url ? (
                  <img src={formData.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><UserRound className="w-12 h-12 text-muted-foreground" /></div>
                )}
              </div>
              <input type="file" accept="image/*" onChange={handlePhotoChange} ref={fileInputRef} className="hidden" />
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button type="button" onClick={() => fileInputRef.current?.click()} className="text-sm text-primary hover:underline flex items-center gap-1">
                  <Camera className="w-4 h-4" />
                  {language === 'es' ? 'Cambiar foto' : 'Change photo'}
                </button>
                {formData.photo_url && (
                  <button type="button" onClick={handleRemovePhoto} className="text-sm text-destructive hover:underline">
                    {language === 'es' ? 'Eliminar foto' : 'Remove photo'}
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('name')}</label>
              <input type="text" value={formData.name} readOnly className="input-brutal w-full px-4 py-3 cursor-default bg-muted/40" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder="usuario@dominio.com"
                data-testid="profile-email-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Si lo cambiás, vas a tener que volver a iniciar sesión con el nuevo email.</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('nationality')}</label>
              <select value={formData.nationality} onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} className="input-brutal w-full px-4 py-3">
                <option value="">Seleccionar...</option>
                {countries.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('university')}</label>
              <input type="text" value={formData.university} onChange={(e) => setFormData({ ...formData, university: e.target.value })} className="input-brutal w-full px-4 py-3" placeholder="Ej: Universidad de Buenos Aires" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Estudios</label>
              <input type="text" value={formData.studies} onChange={(e) => setFormData({ ...formData, studies: e.target.value })} className="input-brutal w-full px-4 py-3" placeholder="Ej: Lic. en Sistemas, 3er año" data-testid="profile-studies-input" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">{t('interests')}</label>
              <input type="text" value={formData.interests} onChange={(e) => setFormData({ ...formData, interests: e.target.value })} className="input-brutal w-full px-4 py-3" placeholder="Ej: Tecnología, Viajes, Música" />
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-foreground">{t('socialNetworks')}</label>
              <div className="flex items-center gap-2">
                <Instagram className="w-5 h-5 text-muted-foreground" />
                <input type="text" value={formData.instagram} onChange={(e) => setFormData({ ...formData, instagram: e.target.value })} className="input-brutal flex-1 px-4 py-2 text-sm" placeholder="username" />
              </div>
              <div className="flex items-center gap-2">
                <Facebook className="w-5 h-5 text-muted-foreground" />
                <input type="text" value={formData.facebook} onChange={(e) => setFormData({ ...formData, facebook: e.target.value })} className="input-brutal flex-1 px-4 py-2 text-sm" placeholder="facebook.com/..." />
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-muted-foreground" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M16.7 5.2a4.8 4.8 0 0 0 3.1 1.2v3.1a7.9 7.9 0 0 1-3.1-.7v6.1a5.9 5.9 0 1 1-5.9-5.9c.3 0 .7 0 1 .1v3.2a2.7 2.7 0 1 0 1.8 2.6V3.8h3.1v1.4Z" fill="currentColor" />
                </svg>
                <input type="text" value={formData.tiktok} onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })} className="input-brutal flex-1 px-4 py-2 text-sm" placeholder="TikTok username" />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground text-sm">{t('shareProfile')}</p>
                <p className="text-xs text-muted-foreground">{t('shareProfileDesc')}</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, share_profile: !formData.share_profile })}
                className={`edit-profile-modal__privacy-toggle w-12 h-6 rounded-full transition-colors ${formData.share_profile ? 'bg-accent-mint' : 'bg-border'}`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${formData.share_profile ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 py-3 border border-border rounded-lg font-medium text-foreground hover:bg-muted">{t('cancel')}</button>
              <button type="submit" disabled={loading} className="edit-profile-modal__save btn-brutal flex-1 py-3 disabled:opacity-50">{loading ? '...' : t('save')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


function ReservationsTab({ user, t, language, theme = 'dark' }) {
  const [spaces, setSpaces] = useState([]);
  const [myReservations, setMyReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpace, setSelectedSpace] = useState(null);
  
  
  const [editingReservation, setEditingReservation] = useState(null);

  const load = async () => {
    try {
      const [sp, rs] = await Promise.all([api.getSpaces(), api.getReservations()]);
      setSpaces(sp);
      setMyReservations(rs);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Cancelar esta reserva?' : 'Cancel this reservation?')) return;
    try {
      await api.cancelReservation(id);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const upcomingReservations = getUpcomingGuestReservations(myReservations);

  return (
    <div className="space-y-4 animate-enter" data-testid="guest-reservations-tab">
      <div>
        <h2 className="font-heading font-bold text-2xl text-foreground">
          {language === 'es' ? 'Reservar espacios' : 'Book spaces'}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {language === 'es' ? 'Mis próximas reservas' : 'My upcoming bookings'}
        </p>
      </div>

      {upcomingReservations.length > 0 && (
        <div className="space-y-2">
          {upcomingReservations.map(r => (
            <div key={r.id} className="card-brutal p-3 flex items-center justify-between gap-2" data-testid={`my-reservation-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{r.space_name}</p>
                <p className="text-xs text-muted-foreground">{formatDateAR(r.date, language === 'es' ? 'es-AR' : 'en-US')} · {r.start_time}–{r.end_time}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => { setSelectedSpace(null); setEditingReservation(r); }}
                  className="text-xs px-3 py-1.5 border-2 border-foreground rounded-lg bg-surface hover:bg-accent-yellow"
                  data-testid={`edit-reservation-${r.id}`}
                >
                  {language === 'es' ? 'Editar' : 'Edit'}
                </button>
                <button
                  onClick={() => cancel(r.id)}
                  className="text-xs px-3 py-1.5 border-2 border-foreground rounded-lg bg-destructive text-white"
                  data-testid={`cancel-reservation-${r.id}`}
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
          <LoadingIndicator label={language === 'es' ? 'Cargando...' : 'Loading...'} className="py-8" />
      ) : spaces.length === 0 ? (
        <div
          className={`p-8 text-center rounded-xl border border-border bg-surface${theme === 'light' ? ' rounded-2xl' : ''}`}
          style={theme === 'light' ? {
            boxShadow: '0 1px 2px rgba(15, 23, 41, 0.04), 0 4px 12px rgba(15, 23, 41, 0.05)',
          } : undefined}
        >
          <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            {language === 'es' ? 'Tu casa no tiene espacios reservables todavía.' : 'Your house has no bookable spaces yet.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {spaces.map(s => (
            <button
              key={s.id}
              onClick={() => setSelectedSpace(s)}
              className="card-brutal p-3 text-left flex items-center gap-3 w-full min-w-0"
              data-testid={`space-${s.id}`}
            >
              <div className="w-10 h-10 rounded-lg bg-accent-yellow border-2 border-foreground flex items-center justify-center text-xl flex-shrink-0">
                {(() => {
                  const SpaceIcon = getSpaceIcon(s.space_type);
                  return <SpaceIcon className="w-6 h-6" aria-hidden="true" />;
                })()}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-foreground text-sm truncate">{s.name}</h3>
                <p className="text-xs text-muted-foreground truncate">{s.description || s.type_label}</p>
                <p className="text-xs text-primary mt-0.5">
                  {String(s.open_hour).padStart(2, '0')}:00 – {String(s.close_hour).padStart(2, '0')}:00
                  {s.capacity ? ` · ${language === 'es' ? 'Cap.' : 'Cap.'} ${s.capacity}` : ''}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {selectedSpace && (
        <ReservationModal
          space={selectedSpace}
          onClose={() => setSelectedSpace(null)}
          onBooked={() => { setSelectedSpace(null); load(); }}
          language={language}
        />
      )}
      {editingReservation && spaces.find(space => space.id === editingReservation.space_id) && (
        <ReservationModal
          space={spaces.find(space => space.id === editingReservation.space_id)}
          initialReservation={editingReservation}
          onClose={() => setEditingReservation(null)}
          onBooked={() => { setEditingReservation(null); load(); }}
          language={language}
        />
      )}
    </div>
  );
}

function ReservationModal({ space, initialReservation, onClose, onBooked, language }) {
  useBodyScrollLock();
  const isEdit = !!initialReservation;
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(initialReservation?.date || today);
  const [slot, setSlot] = useState(
    initialReservation ? { start: initialReservation.start_time, end: initialReservation.end_time } : null
  );
  const [notes, setNotes] = useState('');
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  
  const slotMinutes = space.slot_minutes || 60;
  const slotHours = slotMinutes / 60;
  const slots = [];
  for (let h = space.open_hour; h < space.close_hour; h += slotHours) {
    
    
    
    const rawEnd = h + slotHours;
    const end = Math.min(rawEnd, space.close_hour);
    
    if (end - h < 0.5) continue;
    const startH = Math.floor(h);
    const startM = Math.round((h - startH) * 60);
    const endH = Math.floor(end);
    const endM = Math.round((end - endH) * 60);
    slots.push({
      start: `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`,
      end: `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`,
    });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      try {
        const r = await api.getReservations({ space_id: space.id, date });
        setExisting(r);
        
        
        if (!isEdit) setSlot(null);
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, space.id]);

  const isSlotFull = (s) => {
    const occupiedCount = existing.filter(r => r.start_time < s.end && r.end_time > s.start).length;
    return occupiedCount >= (space.capacity || 1);
  };

  const handleSubmit = async () => {
    if (!slot) {
      setError(language === 'es' ? 'Elegí un horario' : 'Pick a time slot');
      return;
    }
    setError('');
    setLoading(true);
    try {
      if (isEdit) {
        await api.updateReservation(initialReservation.id, {
          date,
          start_time: slot.start,
          end_time: slot.end,
        });
      } else {
        await api.createReservation({
          space_id: space.id,
          date,
          start_time: slot.start,
          end_time: slot.end,
          notes,
        });
      }
      onBooked();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
      setLoading(false);
    }
  };

  return createPortal((
    <div className="reservation-modal-backdrop fixed inset-0 bg-black/70 z-50 modal-backdrop" data-testid="reservation-modal">
      <div className="bg-surface border-2 border-foreground rounded-2xl w-full max-w-lg overflow-y-auto modal-card">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-accent-yellow border-2 border-foreground rounded-lg flex items-center justify-center text-2xl">
              {(() => {
                const SpaceIcon = getSpaceIcon(space.space_type);
                return <SpaceIcon className="w-7 h-7" aria-hidden="true" />;
              })()}
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl">{space.name}</h2>
              <p className="text-xs text-muted-foreground">{space.description}</p>
            </div>
          </div>

          {error && <div className="mb-3 p-2 bg-destructive/20 border border-destructive rounded-lg text-sm">{error}</div>}

          <label className="block text-sm font-medium mb-2">{language === 'es' ? 'Fecha' : 'Date'}</label>
          <NativeDateField
            value={date}
            min={today}
            onChange={(e) => setDate(e.target.value)}
            data-testid="reservation-date-input"
            language={language}
            className="mb-4"
          />

          <label className="block text-sm font-medium mb-2">{language === 'es' ? 'Horario' : 'Time slot'}</label>
          <div className="grid grid-cols-3 gap-2 mb-4 max-h-60 overflow-y-auto p-1">
            {slots.map((s, idx) => {
              const full = isSlotFull(s);
              const selected = slot && slot.start === s.start;
              return (
                <button
                  key={idx}
                  disabled={full}
                  onClick={() => setSlot(s)}
                  className={`p-2 text-xs rounded-lg border-2 border-foreground font-medium transition-all ${
                    full ? 'bg-muted text-muted-foreground line-through cursor-not-allowed opacity-50' :
                    selected ? 'bg-accent-mint shadow-brutal-sm' : 'bg-surface hover:bg-accent-yellow'
                  }`}
                  data-testid={`slot-${s.start}`}
                >
                  {s.start}–{s.end}
                </button>
              );
            })}
          </div>

          <label className="block text-sm font-medium mb-2">{language === 'es' ? 'Notas (opcional)' : 'Notes (optional)'}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="input-brutal w-full px-3 py-2 mb-4 resize-none"
            placeholder={language === 'es' ? 'Algo que debamos saber...' : 'Anything we should know...'}
            data-testid="reservation-notes-input"
          />

          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-3 border-2 border-foreground rounded-lg hover:bg-muted">
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !slot}
              className="btn-brutal flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="confirm-reservation-button"
            >
              <Check className="w-4 h-4" />
              {loading ? '...' : (language === 'es' ? 'Reservar' : 'Book')}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}

function MessagesModalSelector({ directThreads, user, language, onClose, onSelectThread, embedded = false }) {
  const [housemates, setHousemates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const list = await api.getHousemates();
        setHousemates(list || []);
      } catch (e) {
        console.error('Error fetching housemates:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleStartChat = async (userId) => {
    try {
      const thread = await api.createDirectThread(userId);
      onSelectThread({ ...thread, isAdminConversation: false });
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al iniciar chat');
    }
  };

  const panel = (
      <div className="messages-modal-selector__panel bg-surface w-full max-w-md modal-card p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 border-b border-border pb-3">
          <h3 className="messages-modal-selector__title font-heading font-bold text-lg text-foreground">
            {language === 'es' ? 'Mensajes' : 'Messages'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg text-foreground"><X className="w-5 h-5" /></button>
        </div>

        {directThreads.length > 0 && (
          <div className="messages-modal-selector__section mb-5">
            <p className="messages-modal-selector__section-title text-xs uppercase font-bold text-muted-foreground tracking-wide mb-2 font-heading">
              {language === 'es' ? 'Administración' : 'Administration'}
            </p>
            <div className="space-y-2">
              {directThreads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => onSelectThread({ ...t, isAdminConversation: true })}
                  className="messages-modal-selector__admin-preview w-full p-3 flex items-center justify-between text-left transition-colors"
                >
                  <div className="messages-modal-selector__conversation flex items-center gap-3 min-w-0">
                    <span className="messages-modal-selector__admin-avatar" aria-hidden="true">
                      <img src="/logo192.png" alt="" className="brand-logo-adaptive" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-heading font-semibold text-sm">{language === 'es' ? 'Administración' : 'Administration'}</span>
                      <span className="block text-xs text-muted-foreground truncate max-w-[200px]">{t.last_message_text || (language === 'es' ? 'Sin mensajes' : 'No messages')}</span>
                    </span>
                  </div>
                  {t.unread_guest > 0 && (
                    <span className="bg-destructive text-white text-xs px-2 py-0.5 rounded-full font-bold">
                      {t.unread_guest}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="messages-modal-selector__section-title text-xs uppercase font-bold text-muted-foreground tracking-wide mb-2 font-heading">
            {language === 'es' ? 'Compañeros' : 'Housemates'}
          </p>
          {loading ? (
            <LoadingIndicator label="Cargando compañeros…" className="py-3" />
          ) : housemates.length === 0 ? (
            <div className="messages-modal-selector__empty-state">
              <UsersRound className="messages-modal-selector__empty-icon" aria-hidden="true" />
              <p className="text-sm font-medium text-foreground">{language === 'es' ? 'No hay compañeros disponibles' : 'No housemates available'}</p>
              <p className="text-xs text-muted-foreground">{language === 'es' ? 'Todavía no hay otros huéspedes con el perfil público.' : 'There are no other housemates with a public profile yet.'}</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {housemates.map((h) => (
                <button
                  key={h.id}
                  onClick={() => handleStartChat(h.id)}
                  className="w-full p-3 border border-border rounded-xl flex items-center justify-between hover:bg-muted transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-yellow border border-foreground flex items-center justify-center font-bold text-xs uppercase">
                      {h.name?.[0] || 'U'}
                    </div>
                    <div>
                      <p className="font-heading font-medium text-sm text-foreground">{h.name}</p>
                      {h.room_number && <p className="text-xs text-muted-foreground">Habitación {h.room_number}</p>}
                    </div>
                  </div>
                  <MessageSquare className="w-4 h-4 text-primary" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
  );

  if (embedded) return panel;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="messages-modal-selector">
      {panel}
    </div>
  );
}
